/**
 * Scoping a band so it survives a move between two drafts of the SAME tenant.
 *
 * Measured on two real drafts of one tenant (fe-reports/fav-rewrap-proof-01.md):
 * the dsh engine's skin block is byte-identical across drafts of a tenant, but
 * the writer invents fresh class names every run. A band lifted from draft 1
 * therefore finds almost none of its rules in draft 2 and arrives stripped -
 * buttons render as underlined links, chip rows as stacked lists, art areas
 * empty. Three rules fix that, and each one is here because a render failed
 * without it:
 *
 *   1. carry the subset of the source stylesheet that targets the band, plus
 *      the custom properties those rules read (two drafts of one tenant
 *      declare --t-body and --t-h2 with DIFFERENT values, so a band that
 *      keeps its source values is the only band that keeps its type scale);
 *   2. rename the band's PRIVATE names into a per-favorite namespace: inner
 *      ids, class names and @keyframes. Prefixing selectors is not enough: the
 *      destination's `.hero__copy{grid-column:1/span 8}` reached a band whose
 *      own page had no rule for that class and broke its grid;
 *   3. carry the band's element-selector rules too, scoped to the band, or the
 *      destination's bare `h1,h2,h3` wins and the band changes typeface.
 *
 * The band's PUBLIC identity is not private. The outer `<section id>` is the
 * canonical boundary every consumer keys on - DraftLayout's direct-child scan,
 * the spine, the gate readers, `#hero` CTAs - so the root id is kept verbatim
 * and the marker rides on that same section. There is NO wrapper div. A source
 * `#hero` rule is re-scoped to that marked section (`[data-fav-band="ns"]#hero`),
 * not renamed away. On apply the destination's own rules that target the
 * OUTGOING band (`#hero h1`) are retired surgically, arm by arm, and the owned
 * stylesheet is replaced per slot (one owned `<style>` per slot) rather than
 * appended, so repeated swaps neither strand a wrapper nor stack blocks.
 *
 * Two things this must never do, both binding constraints on the feature:
 * it never emits an `.lp-page` wrapper or a token block (that mechanic is dead
 * on dsh - the class matches no element on any dsh page), and it refuses a
 * cross-tenant apply outright. Cross-tenant renders under exactly this fix,
 * which is why the refusal is in the code and not in a comment.
 */

import {
  skipCommentOrString,
  splitTopLevel,
  matchBrace,
  matchParen,
  nextTopBrace,
  firstAtName,
  splitLeadingStatements,
  effectiveSelector,
  cssBlocks,
  stripComments,
  varsRead,
  compounds,
  compoundTokens,
  namedTokens,
  idTokensOf,
  insertMarker,
  joinCompounds,
  stripLeadingComments,
  matchBracket,
  pseudoBefore,
  TARGET_PSEUDOS,
  type Compound,
} from './cssScan';
import {
  tokenizeTags,
  applyMarkupRewrite,
  collectAttrRefs,
  type StartTag,
  type IdRef,
} from './markup';
import {
  buildTypographyFingerprint,
  checkTypographyGate,
  transportTypography,
  renameFontFamilies,
  type TypographyFingerprint,
} from './typographyGate';
import {
  attrSelectorTokens,
  attrOpCouldMatch,
  bandTagSet,
  unsafeContextReason,
  matchesContext,
  statelessContext,
  widenForMatch,
  describeContext,
} from './selectorScope';

/** A band lifted out of one draft, ready to drop into another draft of the same tenant. */
export type BandSnapshot = {
  /** Namespace stamped on the band's own section marker and prefixed onto its private names. */
  namespace: string;
  /** Band id as it appears in the source draft (`hero`, `final-cta`, ...). Kept canonical. */
  bandId: string;
  /** Tenant the band was taken from. Checked on apply; never used for styling. */
  tenantId: string;
  /** The band's markup: its own `<section>`, canonical id kept, private names renamed, marker on the section. */
  html: string;
  /** The rules that target the band, private names renamed and re-scoped to the marked section. */
  css: string;
  /** Fingerprint of the SOURCE page's typography; checked on apply. Absent on legacy objects → refuse. */
  typography?: TypographyFingerprint;
  /**
   * Page-context requirements the carried rules depend on. Each is a `matches()`
   * selector (`.lp--form-first *`, `.dshc-header--plate + *`) that the band's root
   * satisfied in the source; apply refuses unless the destination places it the same way.
   */
  contexts?: string[];
};

export type SnapshotFailure = { ok: false; reason: string; detail?: string };
export type SnapshotResult = { ok: true; snapshot: BandSnapshot } | SnapshotFailure;
export type ApplyResult = { ok: true; html: string; retired?: string[] } | SnapshotFailure;

const WRAPPER_ATTR = 'data-fav-band';
const SLOT_ATTR = 'data-fav-slot';

/** Thrown when band styling sits in a construct a swap cannot carry/split; caught at the boundary. */
class BandRefusal {
  reason: string;
  constructor(reason: string) {
    this.reason = reason;
  }
}

// ---------------------------------------------------------------------------
// Low-level scanners: comment- and string-aware so <style> bytes never lie.
// ---------------------------------------------------------------------------

/** Web-font stylesheet hosts a linked/@import sheet may use and still pass (fonts are the typography gate's concern). */
const FONT_CSS_HOSTS = ['fonts.googleapis.com', 'fonts.bunny.net', 'use.typekit.net', 'fonts.cdnfonts.com'];
const isFontCss = (href: string): boolean => {
  const h = href.toLowerCase();
  return FONT_CSS_HOSTS.some((host) => h.includes(host));
};

/** Real-element view: `<style>`/`<link>`/`@import` inside comments or `<script>` are inert markup, not stylesheets. */
function withoutInertMarkup(s: string): string {
  return s.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ').replace(/<!--[\s\S]*?-->/g, ' ');
}

/**
 * End of an `@import` statement: the first top-level `;` at or past `from`, outside
 * strings and parens. `indexOf(';')` alone stops inside a Google-Fonts `url(…wght@400;500…)`,
 * so the href it extracts is empty; a paren/string-aware scan does not.
 */
function importStatementEnd(css: string, from: number): number {
  let i = from;
  while (i < css.length) {
    const skip = skipCommentOrString(css, i);
    if (skip >= 0) { i = skip; continue; }
    if (css[i] === '(') { i = matchParen(css, i) + 1; continue; }
    if (css[i] === ';') return i;
    i += 1;
  }
  return css.length;
}

/** `@import`ed stylesheet urls at the top level of `css`, comment/string aware. */
function importUrls(css: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < css.length) {
    const skip = skipCommentOrString(css, i);
    if (skip >= 0) { i = skip; continue; }
    if (css.startsWith('@import', i)) {
      const end = importStatementEnd(css, i + 7);
      const stmt = css.slice(i + 7, end);
      const m = /url\(\s*["']?([^"')]+?)["']?\s*\)|["']([^"']+)["']/.exec(stmt);
      out.push((m?.[1] ?? m?.[2] ?? '').trim());
      i = end + 1;
      continue;
    }
    i += 1;
  }
  return out;
}

/**
 * Every source `<style>` body, comment-stripped and concatenated, each wrapped in
 * its own `media` condition (`media="all"`/empty stay unconditional). A band's own
 * in-band `<style>` rules are source CSS like any other and travel through the normal
 * keep/scope path; `stripInBandStyles` removes those `<style>` elements from the markup
 * so nothing lands unscoped, and `applyBandSnapshot` proves the OUTGOING band's in-band
 * rules style only that band before its span is replaced. An in-band `<style media>`
 * is wrapped as `@media` just like a page-level one. Refuses a linked or `@import`ed
 * stylesheet that is not a web font, because the band's styling cannot be proven
 * without it. Another slot's owned block (`data-fav-band=…`) is not source styling and
 * is skipped.
 */
function styleText(doc: string): string {
  const cleaned = withoutInertMarkup(doc);
  // Read `<link>`/`<style>` attributes through the tag tokenizer, so an UNQUOTED value
  // (`<style media=print>`, `<link rel=stylesheet>`) is read exactly like a quoted one — a raw
  // regex missed unquoted `media`, letting print-only styling land unconditionally on screen.
  const tags = tokenizeTags(cleaned);

  for (const t of tags) {
    if (t.kind !== 'start' || t.name !== 'link') continue;
    const rel = t.attrs.find((a) => a.nameLower === 'rel')?.value ?? '';
    if (!/\bstylesheet\b/i.test(rel)) continue;
    const href = (t.attrs.find((a) => a.nameLower === 'href')?.value ?? '').trim();
    if (isFontCss(href)) continue;
    throw new BandRefusal(
      `the source links the stylesheet ${JSON.stringify(href || '(no href)')}, whose rules a band swap cannot prove; nothing was changed`,
    );
  }

  const parts: string[] = [];
  for (let i = 0; i < tags.length; i += 1) {
    const t = tags[i];
    if (t.kind !== 'start' || t.name !== 'style') continue;
    if (t.attrs.some((a) => a.nameLower === WRAPPER_ATTR)) continue; // another slot's owned block
    const endTag = tags.slice(i + 1).find((x) => x.kind === 'end' && x.name === 'style');
    const body = cleaned.slice(t.end, endTag ? endTag.start : cleaned.length);
    const media = (t.attrs.find((a) => a.nameLower === 'media')?.value ?? '').trim();
    parts.push(media && media.toLowerCase() !== 'all' ? `@media ${media}{${body}}` : body);
  }
  const css = stripComments(parts.join('\n'));

  for (const url of importUrls(css)) {
    if (!isFontCss(url)) {
      throw new BandRefusal(
        `the source imports the stylesheet ${JSON.stringify(url)}, whose rules a band swap cannot prove; nothing was changed`,
      );
    }
  }
  if (selectorUsesEscape(css)) throw new BandRefusal(ESCAPED_SELECTOR_REASON);
  return css;
}

const ESCAPED_SELECTOR_REASON =
  'the page styles elements through a selector written with CSS escapes (a backslash), which a band swap cannot check class by class; nothing was changed';

/** Does any rule or at-rule prelude in `css`, at any nesting depth, contain a CSS escape? */
function selectorUsesEscape(css: string): boolean {
  return cssBlocks(css).some((b) => b.prelude.includes('\\') || (nextTopBrace(b.body, 0) >= 0 && selectorUsesEscape(b.body)));
}

/**
 * Remove the band's own in-band `<style>…</style>` elements, spliced by the tokenizer's
 * tag spans (never by regex, so a `</style>` inside a comment or script cannot mis-cut).
 * Their rules already travelled into the scoped owned block via `styleText`; dropping the
 * elements keeps them from landing unscoped. Every other byte stays identical. A `<style>`
 * carrying `data-fav-band` is another slot's owned block, not the band's own styling, so
 * it is left in place (as `styleText` also skips it).
 */
function stripInBandStyles(fragment: string): string {
  const tags = tokenizeTags(fragment);
  const spans: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < tags.length; i += 1) {
    const t = tags[i];
    if (t.kind !== 'start' || t.name !== 'style') continue;
    if (t.attrs.some((a) => a.nameLower === WRAPPER_ATTR)) continue; // another slot's owned block
    const endTag = tags.slice(i + 1).find((x) => x.kind === 'end' && x.name === 'style');
    spans.push({ start: t.start, end: endTag ? endTag.end : fragment.length });
  }
  spans.sort((a, b) => b.start - a.start);
  let out = fragment;
  for (const s of spans) out = out.slice(0, s.start) + out.slice(s.end);
  return out;
}

/**
 * The band elements DraftLayout recognises: a top-level `<section>`, `<header>`,
 * `<footer>`, `<nav>` or `<aside>`. Header/footer are bands too now that the
 * writer owns every band; on many drafts they are `<header id>`/`<footer id>`
 * elements rather than sections.
 */
const BAND_TAGS = new Set(['section', 'header', 'footer', 'nav', 'aside']);

/**
 * Every top-level band element of the document, with its id and byte span.
 *
 * Built on the tag tokenizer, so a band tag inside a comment, a `<script>` or
 * a `<style>` never counts, and an unquoted `id=hero` is seen. Depth-tracked
 * over ALL band tags, so a band nested inside another band element (a `<nav>`
 * inside a `<header>`, a `<section>` inside a `<footer>`) is not top-level and is
 * not returned. Band ids are matched case-insensitively (the one id-case rule;
 * see idCounts and the slot).
 */
function scanBands(doc: string): Array<{ id: string; start: number; end: number }> {
  const out: Array<{ id: string; start: number; end: number }> = [];
  let depth = 0;
  let current: { id: string; start: number } | null = null;
  for (const tag of tokenizeTags(doc)) {
    if (!BAND_TAGS.has(tag.name)) continue;
    if (tag.kind === 'start') {
      if (tag.selfClosing) continue; // a self-closed band has no content; not a band
      if (depth === 0) {
        const id = (tag.attrs.find((a) => a.nameLower === 'id')?.value ?? '').trim().toLowerCase();
        current = { id, start: tag.start };
      }
      depth += 1;
    } else if (depth > 0) {
      depth -= 1;
      if (depth === 0 && current) {
        out.push({ id: current.id, start: current.start, end: tag.end });
        current = null;
      }
    }
  }
  return out;
}

/** Band ids in render order, as the document actually carries them. */
export function listBands(doc: string): string[] {
  return scanBands(doc)
    .map((band) => band.id)
    .filter(Boolean);
}

function findBand(doc: string, bandId: string): { start: number; end: number } | null {
  const wanted = bandId.trim().toLowerCase();
  return scanBands(doc).find((band) => band.id === wanted) ?? null;
}

/** Class tokens on REAL elements (exact `class` attribute), so shortcode/script text is never mined. */
function classesIn(fragment: string): Set<string> {
  const out = new Set<string>();
  for (const tag of tokenizeTags(fragment)) {
    if (tag.kind !== 'start') continue;
    const cls = tag.attrs.find((a) => a.nameLower === 'class')?.value;
    if (cls) for (const c of cls.split(/\s+/)) if (c) out.add(c);
  }
  return out;
}

/** Ids on REAL elements (exact `id` attribute only — `data-id` is not an id, a shortcode `id="1"` is text). */
function idsIn(fragment: string): Set<string> {
  const out = new Set<string>();
  for (const tag of tokenizeTags(fragment)) {
    if (tag.kind !== 'start') continue;
    const id = tag.attrs.find((a) => a.nameLower === 'id')?.value?.trim();
    if (id) out.add(id);
  }
  return out;
}

/** `--name: value` pairs declared on :root anywhere in the source (unconditional). */
function rootDeclarations(css: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const { prelude, body } of cssBlocks(css)) {
    if (prelude !== ':root' && !prelude.endsWith(':root')) continue;
    for (const declaration of splitTopLevel(body, ';')) {
      const colon = declaration.indexOf(':');
      const name = declaration.slice(0, colon).trim();
      if (colon > 0 && /^--[\w-]+$/.test(name)) out.set(name, declaration.slice(colon + 1).trim());
    }
  }
  return out;
}

/** Wrap `inner` in each condition prelude, innermost last: `["@media A","@supports B"]` → `@media A{@supports B{inner}}`. */
function wrapConditions(inner: string, conditions: string[]): string {
  return conditions.reduceRight((acc, cond) => `${cond}{${acc}}`, inner);
}

/** Every @keyframes in the source (top-level or inside @media/@supports), with the conditions it sits under. */
function collectKeyframes(css: string): Array<{ name: string; body: string; conditions: string[] }> {
  const out: Array<{ name: string; body: string; conditions: string[] }> = [];
  const walk = (text: string, conditions: string[]): void => {
    for (const block of cssBlocks(text)) {
      const sel = effectiveSelector(block.prelude);
      const kf = /^@keyframes\s+(-?[_a-zA-Z][\w-]*)/i.exec(sel);
      if (kf) { out.push({ name: kf[1], body: block.body, conditions }); continue; }
      if (/^@(media|supports)\b/i.test(sel)) walk(block.body, [...conditions, sel]);
    }
  };
  walk(css, []);
  return out;
}

/** `:root{--x:…}` custom properties declared INSIDE @media/@supports, with their condition chain, in source order. */
function conditionalRootDeclarations(css: string): Array<{ conditions: string[]; name: string; value: string }> {
  const out: Array<{ conditions: string[]; name: string; value: string }> = [];
  const walk = (text: string, conditions: string[]): void => {
    for (const block of cssBlocks(text)) {
      const sel = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(sel)) { walk(block.body, [...conditions, sel]); continue; }
      if (!conditions.length) continue; // unconditional :root handled by rootDeclarations
      const isRoot = splitTopLevel(sel, ',').some((a) => { const t = a.trim(); return t === ':root' || t.endsWith(':root'); });
      if (!isRoot) continue;
      for (const m of block.body.matchAll(/(--[\w-]+)\s*:\s*([^;]+)/g)) out.push({ conditions, name: m[1], value: m[2].trim() });
    }
  };
  walk(css, []);
  return out;
}

/** Custom properties registered with `@property` in the source. A registration is global and cannot be scoped to a band. */
function propertyRegistrations(css: string): Set<string> {
  const out = new Set<string>();
  for (const { prelude } of cssBlocks(css)) {
    const m = /^@property\s+(--[\w-]+)/i.exec(effectiveSelector(prelude));
    if (m) out.add(m[1]);
  }
  return out;
}

/** Every `:root` custom-property declaration in SOURCE ORDER, unconditional (`conditions:[]`) and conditional alike. */
function rootDeclarationsInOrder(css: string): Array<{ conditions: string[]; name: string; value: string }> {
  const out: Array<{ conditions: string[]; name: string; value: string }> = [];
  const walk = (text: string, conditions: string[]): void => {
    for (const block of cssBlocks(text)) {
      const sel = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(sel)) { walk(block.body, [...conditions, sel]); continue; }
      const isRoot = splitTopLevel(sel, ',').some((a) => { const t = a.trim(); return t === ':root' || t.endsWith(':root'); });
      if (!isRoot) continue;
      for (const m of block.body.matchAll(/(--[\w-]+)\s*:\s*([^;]+)/g)) out.push({ conditions, name: m[1], value: m[2].trim() });
    }
  };
  walk(css, []);
  return out;
}

/**
 * Custom properties the band inherits from an ANCESTOR rule other than `:root` (which
 * `rootDeclarations` carries): `body{--pad}`, `html{--pad}`, `.page{--pad}`, any rule whose widened
 * selector matches an ancestor of the band's root in the source DOM. The swap carries only `:root`,
 * so such a var would silently take the DESTINATION's value (and an ancestor declaration also
 * overrides the carried `:root` one). Map name -> the supplier selector, for a named refusal.
 * With no source root to test, a bare `html`/`body`/`main` rule counts (fail closed).
 */
function ancestorVarSuppliers(css: string, root: Element | null): Map<string, string> {
  const out = new Map<string, string>();
  const suppliesAncestor = (arm: string): boolean => {
    if (!root) return /^(html|body|main)$/i.test(arm);
    const sel = widenForMatch(arm);
    for (let el = root.parentElement; el; el = el.parentElement) if (matchesContext(el, sel) !== false) return true;
    return false;
  };
  const walk = (text: string): void => {
    for (const block of cssBlocks(text)) {
      const sel = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(sel)) { walk(block.body); continue; }
      if (sel.startsWith('@') || block.body.includes('{')) continue;
      const decls = [...block.body.matchAll(/(--[\w-]+)\s*:\s*[^;]+/g)].map((m) => m[1]);
      if (!decls.length) continue;
      for (const raw of splitTopLevel(sel, ',')) {
        const arm = raw.trim();
        if (!arm || /:root\s*$/i.test(arm) || !suppliesAncestor(arm)) continue;
        for (const d of decls) if (!out.has(d)) out.set(d, arm);
      }
    }
  };
  walk(css);
  return out;
}

type Spec = [number, number, number];
/** Compare two specificities: >0 when `a` outranks `b`. */
function cmpSpec(a: Spec, b: Spec): number {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}
const LEGACY_PSEUDO_ELEMENTS = new Set(['before', 'after', 'first-line', 'first-letter']);
/**
 * CSS specificity of a selector arm: `[id, class/attr/pseudo-class, type/pseudo-element]`.
 * `:where()` contributes zero; `:is()/:matches()/:-webkit-any()/:not()/:has()` take the MAX of their
 * arguments. Used only to decide whether pruning a `:is()` alternative would change specificity.
 */
function selectorSpecificity(sel: string): Spec {
  let a = 0, b = 0, c = 0;
  let i = 0;
  while (i < sel.length) {
    const skip = skipCommentOrString(sel, i);
    if (skip >= 0) { i = skip; continue; }
    const ch = sel[i];
    if (ch === '[') { b += 1; i = matchBracket(sel, i) + 1; continue; }
    if (ch === '#') { const m = /^#-?[_a-zA-Z][\w-]*/.exec(sel.slice(i)); if (m) { a += 1; i += m[0].length; continue; } }
    if (ch === '.') { const m = /^\.-?[_a-zA-Z][\w-]*/.exec(sel.slice(i)); if (m) { b += 1; i += m[0].length; continue; } }
    if (ch === ':') {
      const dbl = sel[i + 1] === ':';
      const m = /^:{1,2}(-?[_a-zA-Z][\w-]*)/.exec(sel.slice(i));
      if (m) {
        const nameL = m[1].toLowerCase();
        let j = i + m[0].length;
        let args: string | null = null;
        if (sel[j] === '(') { const end = matchParen(sel, j); args = sel.slice(j + 1, end); j = end + 1; }
        if (nameL === 'where') { /* zero specificity */ }
        else if (args !== null && (nameL === 'is' || nameL === 'matches' || nameL === '-webkit-any' || nameL === 'not' || nameL === 'has')) {
          let best: Spec = [0, 0, 0];
          for (const inner of splitTopLevel(args, ',')) { const sp = selectorSpecificity(inner.trim()); if (cmpSpec(sp, best) > 0) best = sp; }
          a += best[0]; b += best[1]; c += best[2];
        } else if (dbl || LEGACY_PSEUDO_ELEMENTS.has(nameL)) { c += 1; }
        else { b += 1; }
        i = j; continue;
      }
    }
    if (ch === '*') { i += 1; continue; }
    const tm = /^-?[_a-zA-Z][\w-]*/.exec(sel.slice(i));
    if (tm && (i === 0 || /[\s>+~,(|]/.test(sel[i - 1]))) { c += 1; i += tm[0].length; continue; }
    i += 1;
  }
  return [a, b, c];
}

// ---------------------------------------------------------------------------
// Selector structure: arms, compounds, and the tokens a compound names.
// ---------------------------------------------------------------------------

/**
 * The class/id tokens a selector arm actually TARGETS — the element the rule
 * styles, not the conditions around it. Top-level tokens of each compound, plus
 * (recursively) the targets inside `:is()/:where()/:matches()/:-webkit-any()`
 * (alternatives for that same element). Tokens inside `:has()`, `:not()` and any
 * other functional pseudo are conditions, not targets, and are excluded.
 */
function collectCompoundTargets(compound: string, ids: string[], classes: string[]): void {
  const t = compoundTokens(compound);
  ids.push(...t.ids);
  classes.push(...t.classes);
  let i = 0;
  while (i < compound.length) {
    const skip = skipCommentOrString(compound, i);
    if (skip >= 0) { i = skip; continue; }
    const c = compound[i];
    if (c === '[') { i = matchBracket(compound, i) + 1; continue; }
    if (c === '(') {
      const end = matchParen(compound, i);
      if (TARGET_PSEUDOS.has(pseudoBefore(compound, i))) {
        const inner = compound.slice(i + 1, end);
        for (const arm of splitTopLevel(inner, ',')) {
          for (const c2 of compounds(arm)) collectCompoundTargets(c2.text, ids, classes);
        }
      }
      i = end + 1;
      continue;
    }
    i += 1;
  }
}

/** True if a `:has()` at any nesting depth in `text` has an argument arm that starts with `+` or `~`. */
function hasSiblingLookout(text: string): boolean {
  let i = 0;
  while (i < text.length) {
    const skip = skipCommentOrString(text, i);
    if (skip >= 0) { i = skip; continue; }
    const c = text[i];
    if (c === '[') { i = matchBracket(text, i) + 1; continue; }
    if (c === '(') {
      const end = matchParen(text, i);
      const inner = text.slice(i + 1, end);
      if (pseudoBefore(text, i) === ':has') {
        for (const arm of splitTopLevel(inner, ',')) {
          const a = arm.replace(/^\s+/, '');
          if (a.startsWith('+') || a.startsWith('~')) return true;
        }
      }
      if (hasSiblingLookout(inner)) return true; // a :has(+…) may be nested inside another pseudo
      i = end + 1;
      continue;
    }
    i += 1;
  }
  return false;
}

// ---------------------------------------------------------------------------
// snapshotBand
// ---------------------------------------------------------------------------

export function snapshotBand(args: {
  doc: string;
  bandId: string;
  tenantId: string;
  namespace: string;
}): SnapshotResult {
  const { doc, bandId, tenantId, namespace } = args;
  if (!/^[a-z0-9][a-z0-9-]*$/i.test(namespace)) {
    return { ok: false, reason: `namespace ${JSON.stringify(namespace)} is not a safe css identifier` };
  }
  const span = findBand(doc, bandId);
  if (!span) return { ok: false, reason: `the source draft has no band with id ${JSON.stringify(bandId)}` };
  // Two top-level bands with this id: which one to take cannot be decided. Refuse
  // rather than silently take the first (findBand's choice).
  const sources = scanBands(doc).filter((b) => b.id === bandId.trim().toLowerCase());
  if (sources.length > 1) {
    return { ok: false, reason: `the source draft carries ${sources.length} top-level bands with id ${JSON.stringify(bandId)}; which one to take cannot be decided, so nothing was taken` };
  }

  const fragment = doc.slice(span.start, span.end);
  const rootStart = tokenizeTags(fragment).find((t): t is StartTag => t.kind === 'start');
  const rootAttr = (name: string): string | undefined =>
    rootStart?.attrs.find((a) => a.nameLower === name)?.value ?? undefined;

  // RE-SNAPSHOT: the band was already imported and carries a marker. Reuse or
  // rebase its OWN owned stylesheet; never re-prefix an existing namespace.
  const oldNs = rootAttr('data-fav-band');
  if (oldNs !== undefined) {
    return reSnapshot({ doc, fragment, bandId, tenantId, namespace, oldNs });
  }

  let css: string;
  try {
    css = styleText(doc);
  } catch (e) {
    if (e instanceof BandRefusal) return { ok: false, reason: e.reason };
    throw e;
  }
  if (!css.trim()) {
    return { ok: false, reason: 'the source draft carries no stylesheet, so the band has no rules to take with it' };
  }

  const rootId = (rootAttr('id') ?? '').trim();
  const rootTag = rootStart?.name ?? 'section';
  const rootClassesRaw = new Set((rootAttr('class') ?? '').split(/\s+/).filter(Boolean));

  const classRename = new Map([...classesIn(fragment)].map((c) => [c, `${namespace}-${c}`]));
  const idRename = new Map([...idsIn(fragment)].map((i) => [i, `${namespace}-${i}`]));
  // The band's public identity is canonical: the root id maps to itself, so a
  // carried `#hero` rule still passes membership and re-scopes to this section.
  if (rootId) idRename.set(rootId, rootId);
  const outside = doc.slice(0, span.start) + doc.slice(span.end);
  for (const id of idRename.keys()) {
    if (scriptDependencyRefusal(outside, new Set(), new Set([id]))) idRename.set(id, id);
  }
  const rootClassesRenamed = new Set([...rootClassesRaw].map((c) => classRename.get(c) ?? c));
  const marker = `[${WRAPPER_ATTR}="${namespace}"]`;

  const readVars = new Set<string>();
  const animations = new Set<string>();
  // Page-context requirements discovered on the source side, re-checked on apply.
  const contexts = new Set<string>();
  // The element tags the band effectively contains; a type selector for a tag absent
  // from the band cannot style a band element (an "outside" compound). When the band
  // holds a Gravity Forms slot, the control tags GF injects at publish (input, label,
  // …) count as present so their rules travel.
  const bandTags = bandTagSet(fragment);
  const ROOT_LIKE = new Set(['body', 'html', 'main']);

  const ownsId = (id: string): boolean => idRename.has(id);
  const ownsClass = (c: string): boolean => classRename.has(c);
  /** ids a compound names at top level or through an `[id=…]`/`[id~=…]` selector. */
  const compoundIdsOf = (text: string): string[] => [
    ...compoundTokens(text).ids,
    ...attrSelectorTokens(text).ids,
  ];

  // The source DOM, parsed once, so a page-context arm can be checked with matches().
  let sourceRoot: Element | null | undefined;
  const getSourceRoot = (): Element | null => {
    if (sourceRoot === undefined) {
      sourceRoot = rootId
        ? new DOMParser().parseFromString(doc, 'text/html').getElementById(rootId)
        : null;
    }
    return sourceRoot;
  };

  const renameArm = (arm: string): string => {
    // Rewrite only REAL `.class`/`#id` selector tokens: skip `[…]` attribute contents and quoted
    // strings so an unrelated attribute value (`[data-note=".x"]`) is never mangled into `.ns-x`.
    // Tokens inside `:is()/:not()/…` parens ARE real selector tokens and stay in scope.
    let renamed = '';
    let i = 0;
    while (i < arm.length) {
      const skip = skipCommentOrString(arm, i);
      if (skip >= 0) { renamed += arm.slice(i, skip); i = skip; continue; }
      const ch = arm[i];
      if (ch === '[') { const end = matchBracket(arm, i); renamed += arm.slice(i, end + 1); i = end + 1; continue; }
      if (ch === '.') {
        const m = /^\.(-?[_a-zA-Z][\w-]*)/.exec(arm.slice(i));
        if (m) { renamed += `.${classRename.get(m[1]) ?? m[1]}`; i += m[0].length; continue; }
      }
      if (ch === '#') {
        const m = /^#(-?[_a-zA-Z][\w-]*)/.exec(arm.slice(i));
        if (m) { renamed += `#${idRename.get(m[1]) ?? m[1]}`; i += m[0].length; continue; }
      }
      renamed += ch;
      i += 1;
    }
    // The id/class attribute selectors this deliberately handles track the same rename as `#inner` /
    // `.hero__x`; every other `[…]` (e.g. `[data-note=".x"]`) is left byte-identical by the scan above.
    return renamed.replace(
      /\[\s*(id|class)\s*([~]?=)\s*(?:"([^"]*)"|'([^']*)'|([^\]\s"'=]+))\s*([iIsS])?\s*\]/g,
      (whole, attr: string, op: string, dq: string, sq: string, uq: string, flag: string) => {
        const raw = dq ?? sq ?? uq ?? '';
        const map = attr.toLowerCase() === 'id' ? idRename : classRename;
        return `[${attr}${op}"${map.get(raw) ?? raw}"${flag ? ` ${flag}` : ''}]`;
      },
    );
  };

  /** Re-scope one already-renamed arm to the marked section; null if a root-sibling reaches outside. */
  const scopeRenamedArm = (renamed: string): string | null => {
    const comps = compounds(renamed);
    if (!comps.length) return null;
    const rootIdxs: number[] = [];
    comps.forEach((c, i) => { if (rootId && compoundIdsOf(c.text).includes(rootId)) rootIdxs.push(i); });
    if (rootIdxs.length) {
      const last = rootIdxs[rootIdxs.length - 1];
      const after = comps[last + 1]?.combinator;
      // `#hero + x` / `#hero ~ x` styles something OUTSIDE the band; that arm does not travel.
      if (after === '+' || after === '~') return null;
      const scoped = comps.map((c, i) => (i === last ? { text: insertMarker(c.text, marker), combinator: c.combinator } : c));
      return joinCompounds(scoped);
    }
    const first = compoundTokens(comps[0].text);
    const firstAttr = attrSelectorTokens(comps[0].text);
    const firstClasses = [...first.classes, ...firstAttr.classes];
    const typeOk = first.type === null || first.type === '*' || first.type.toLowerCase() === rootTag;
    const canBeRoot =
      typeOk && first.ids.length === 0 && firstAttr.ids.length === 0 && firstClasses.every((c) => rootClassesRenamed.has(c));
    // A `+`/`~` right after the root-bound compound puts the SUBJECT on a
    // sibling of the band (`section + section`, `.hero + section`); the root form
    // would then style an element outside the band, so only the descendant form travels.
    const rootBoundEscapes = comps[1]?.combinator === '+' || comps[1]?.combinator === '~';
    const forms: string[] = [];
    if (canBeRoot && !rootBoundEscapes) {
      const rootForm = comps.map((c, i) => (i === 0 ? { text: insertMarker(c.text, marker), combinator: c.combinator } : c));
      forms.push(joinCompounds(rootForm));
    }
    forms.push(`${marker} ${renamed.trim()}`);
    return forms.join(', ');
  };

  /** Can this compound (source tokens, any position) be the band's own root element? */
  const compoundCanBeRoot = (text: string): boolean => {
    const t = compoundTokens(text);
    const attr = attrSelectorTokens(text);
    const ids = [...t.ids, ...attr.ids];
    if (rootId && ids.includes(rootId)) return true;
    if (ids.length) return false;
    const classes = [...t.classes, ...attr.classes];
    const typeOk = t.type === null || t.type === '*' || t.type.toLowerCase() === rootTag;
    return typeOk && classes.every((c) => rootClassesRaw.has(c));
  };

  type Reach = 'owned' | 'outside' | 'neutral';
  /**
   * Where a compound's subject can be relative to the band:
   *  - owned:   names a band-owned id/class (a state/modifier class alongside is carried verbatim);
   *  - outside: names an unowned id/class only, or is a type that no band element can be
   *             (`body`/`html`/`:root`/`main`, or a tag absent from the band);
   *  - neutral: no id/class and could match a band element.
   */
  const compoundReach = (text: string): Reach => {
    const ids: string[] = [];
    const classes: string[] = [];
    collectCompoundTargets(text, ids, classes);
    const attr = attrSelectorTokens(text);
    ids.push(...attr.ids);
    classes.push(...attr.classes);
    if (ids.some(ownsId) || classes.some(ownsClass)) return 'owned';
    if (ids.length || classes.length) return 'outside';
    if (/:root\b/i.test(text)) return 'outside';
    const type = compoundTokens(text).type;
    if (type === null || type === '*') return 'neutral';
    const t = type.toLowerCase();
    if (ROOT_LIKE.has(t)) return 'outside';
    return bandTags.has(t) ? 'neutral' : 'outside';
  };

  /** Do any alternatives of a `:is()`/`:where()` in `alt` name a band-owned token? */
  const altTokens = (alt: string): { ids: string[]; classes: string[] } => {
    const ids: string[] = [];
    const classes: string[] = [];
    for (const c of compounds(alt)) {
      collectCompoundTargets(c.text, ids, classes);
      const a = attrSelectorTokens(c.text);
      ids.push(...a.ids);
      classes.push(...a.classes);
    }
    return { ids, classes };
  };
  const altOwned = (alt: string): boolean => {
    const { ids, classes } = altTokens(alt);
    return ids.some(ownsId) || classes.some(ownsClass);
  };
  const altUnowned = (alt: string): boolean => {
    const { ids, classes } = altTokens(alt);
    return ids.some((x) => !ownsId(x)) || classes.some((x) => !ownsClass(x));
  };

  /**
   * Prune `:is()/:where()/:matches()/:-webkit-any()` alternatives that fall outside
   * the band, but only when the pseudo has ≥1 band-owned alternative. Drop
   * outside simple compounds, refuse outside complex ones, and collapse a lone simple
   * survivor so `:is(#hero,#trust)` becomes `#hero`.
   */
  const pruneAlternatives = (arm: string): string => {
    let out = '';
    let i = 0;
    while (i < arm.length) {
      const skip = skipCommentOrString(arm, i);
      if (skip >= 0) { out += arm.slice(i, skip); i = skip; continue; }
      if (arm[i] === '(') {
        const end = matchParen(arm, i);
        const name = pseudoBefore(arm, i);
        const inner = arm.slice(i + 1, end);
        if (TARGET_PSEUDOS.has(name)) {
          const alts = splitTopLevel(inner, ',').map((a) => a.trim()).filter(Boolean);
          if (alts.some(altOwned)) {
            const kept: string[] = [];
            const dropped: string[] = [];
            for (const alt of alts) {
              if (altOwned(alt)) { kept.push(alt); continue; }
              const complex = compounds(alt).length > 1;
              if (complex && altUnowned(alt)) {
                throw new BandRefusal(
                  `the source styles this band through the ${name}() alternative ${JSON.stringify(alt)}, which reaches outside the band; a band swap cannot carry it`,
                );
              }
              if (!complex && altUnowned(alt)) { dropped.push(alt); continue; } // outside simple compound → drop
              kept.push(alt); // neutral (no id/class) → keep
            }
            // Never change specificity. `:where()` is ALWAYS zero-specificity, so collapsing
            // `:where(one)` to a bare compound would RAISE it (the reported bug: `:where(#hero,#trust) h2`
            // must stay at h2's specificity, not #hero's) — keep the wrapper. `:is()/:matches()/
            // :-webkit-any()` take the MAX of their alternatives, so a single simple survivor collapses
            // without changing specificity (`:is(#hero)` ≡ `#hero`); but if a DROPPED alternative
            // outranked every survivor, dropping it would LOWER the `:is` specificity — refuse.
            if (name === ':where') {
              out += `(${kept.join(', ')})`; // zero specificity regardless of contents; never collapse
            } else {
              if (dropped.length) {
                let keptMax: Spec = [0, 0, 0];
                for (const k of kept) { const s = selectorSpecificity(k); if (cmpSpec(s, keptMax) > 0) keptMax = s; }
                for (const d of dropped) {
                  if (cmpSpec(selectorSpecificity(d), keptMax) > 0) {
                    throw new BandRefusal(
                      `the source styles this band through ${name}(), where the outside alternative ${JSON.stringify(d)} outranks every kept alternative, so dropping it would change specificity; a band swap cannot carry it`,
                    );
                  }
                }
              }
              const before = out.slice(0, out.length - name.length);
              const attached = before !== '' && !/[\s>+~]$/.test(before);
              if (kept.length === 1 && compounds(kept[0]).length === 1) {
                if (attached && !'#.[:'.includes(kept[0][0])) {
                  throw new BandRefusal(
                    `the source styles this band through ${JSON.stringify(arm)}, where ${name}(${kept[0]}) is fused to another part of the selector; a band swap cannot carry it`,
                  );
                }
                out = before + kept[0]; // specificity-neutral collapse `:is(one)` → `one`
              } else {
                if (attached) {
                  throw new BandRefusal(
                    `the source styles this band through ${JSON.stringify(arm)}, where ${name}() mixes the band with other alternatives on another part of the selector; a band swap cannot carry it`,
                  );
                }
                out += `(${kept.join(', ')})`;
              }
            }
            i = end + 1;
            continue;
          }
        }
        out += `(${inner})`;
        i = end + 1;
        continue;
      }
      out += arm[i];
      i += 1;
    }
    return out;
  };

  /**
   * The band-root compound of an arm that has an outside ancestor/sibling: scan left
   * from the subject and stop at the first sibling combinator or outside compound, so
   * everything from here to the subject is the band's own descendant tail (the
   * subject can never be a sibling of the band).
   */
  const bandRootIndex = (comps: Compound[], reach: Reach[]): number => {
    let k = comps.length - 1;
    while (k > 0) {
      const comb = comps[k].combinator;
      if (comb === '+' || comb === '~') break;
      if (reach[k - 1] === 'outside') break;
      k -= 1;
    }
    return k;
  };

  /** Carry a page-context arm: context ancestors verbatim, band part marker-scoped and renamed. */
  const carryWithContext = (comps: Compound[], k: number): string => {
    const rootComb = comps[k].combinator;
    const carried: Compound[] = comps.slice(0, k).map((c) => ({ ...c })); // context, verbatim
    if (compoundCanBeRoot(comps[k].text)) {
      carried.push({ text: insertMarker(renameArm(comps[k].text), marker), combinator: rootComb });
    } else {
      // The root section is unnamed here; scope the band's descendants under a standalone
      // marker (only reachable on a descendant/child combinator).
      carried.push({ text: marker, combinator: rootComb });
      carried.push({ text: renameArm(comps[k].text), combinator: ' ' });
    }
    for (const c of comps.slice(k + 1)) carried.push({ text: renameArm(c.text), combinator: c.combinator });
    return joinCompounds(carried);
  };

  /** Can this compound be the band's own root element (so a `:has()` on it looks at OUR siblings)? */
  const canCompoundBeRoot = (compoundText: string, isFirst: boolean): boolean => {
    const t = compoundTokens(compoundText);
    if (rootId && t.ids.includes(rootId)) return true;
    const typeOk = t.type === null || t.type === '*' || t.type.toLowerCase() === rootTag;
    return isFirst && typeOk && t.ids.length === 0 && t.classes.every((c) => rootClassesRaw.has(c));
  };

  const unownedToken = (text: string): boolean => {
    const { ids, classes } = namedTokens(text);
    return ids.some((id) => !idRename.has(id)) || classes.some((c) => !classRename.has(c));
  };

  /**
   * Clarity refusals for an arm whose TARGETS are band-owned but whose CONDITIONS
   * could reach outside the band. Only reached once targets are proven owned, so
   * unrelated page rules never refuse. Names the selector.
   */
  const enforceConditionRefusals = (arm: string): void => {
    const comps = compounds(arm);
    // (a) a `:has(+…)`/`:has(~…)` on a compound that can be the band root looks at OUR siblings.
    comps.forEach((c, idx) => {
      if (canCompoundBeRoot(c.text, idx === 0) && hasSiblingLookout(c.text)) {
        throw new BandRefusal(
          `the source styles this band through ${JSON.stringify(arm)}, whose :has() condition looks outside the band; a band swap cannot carry it`,
        );
      }
    });
    // (b) a complex `:not(…)` and (c) an OTHER functional pseudo, when either names an unowned token.
    const walk = (text: string): void => {
      let i = 0;
      while (i < text.length) {
        const skip = skipCommentOrString(text, i);
        if (skip >= 0) { i = skip; continue; }
        const ch = text[i];
        if (ch === '[') { i = matchBracket(text, i) + 1; continue; }
        if (ch === '(') {
          const end = matchParen(text, i);
          const inner = text.slice(i + 1, end);
          const name = pseudoBefore(text, i);
          if (name === ':not') {
            for (const argArm of splitTopLevel(inner, ',')) {
              if (compounds(argArm.trim()).length > 1 && unownedToken(argArm)) {
                throw new BandRefusal(
                  `the source styles this band through ${JSON.stringify(arm)}, whose :not() condition ${JSON.stringify(argArm.trim())} reaches outside the band; a band swap cannot carry it`,
                );
              }
            }
          } else if (name === ':has' || TARGET_PSEUDOS.has(name)) {
            // conditions/alternatives: recurse for nested :not/:other
          } else if (name) {
            // (c) other functional pseudo, e.g. :nth-child(… of S), ::slotted(S)
            if (unownedToken(inner)) {
              throw new BandRefusal(
                `the source styles this band through ${JSON.stringify(arm)}, whose ${name}() argument reaches outside the band; a band swap cannot carry it`,
              );
            }
          }
          walk(inner);
          i = end + 1;
          continue;
        }
        i += 1;
      }
    };
    walk(arm);
  };

  /**
   * An arm travels when its SUBJECT can be a band element; conditions need not be
   * owned. A compound with an outside ancestor/sibling travels only when the band
   * actually sits in that page context in the source. May refuse.
   */
  const keepArm = (rawArm: string): string | null => {
    const arm = rawArm.trim();
    if (!arm) return null;

    // Risky attribute operators (^= *= $= |=) on id/class that could reach a band token
    // cannot be resolved token-for-token — refuse, naming the selector.
    for (const r of attrSelectorTokens(arm).risky) {
      const pool = r.attr === 'id' ? idRename : classRename;
      for (const tok of pool.keys()) {
        if (attrOpCouldMatch(r.op, r.val, tok)) {
          throw new BandRefusal(
            `the source styles this band through ${JSON.stringify(arm)}, whose [${r.attr}${r.op}"${r.val}"] match a band swap cannot resolve; it cannot carry it`,
          );
        }
      }
    }

    const pruned = pruneAlternatives(arm);
    const comps = compounds(pruned);
    if (!comps.length) return null;
    const reach = comps.map((c) => compoundReach(c.text));
    if (reach[reach.length - 1] === 'outside') return null; // subject cannot be a band element

    if (!reach.includes('outside')) {
      enforceConditionRefusals(pruned);
      return scopeRenamedArm(renameArm(pruned));
    }

    // A page-context ancestor/sibling stands between the page and the band. Find the band
    // root and carry the arm only when the band actually sits in that context (verified on
    // the source DOM), recording the context so apply re-checks it on the destination.
    const k = bandRootIndex(comps, reach);
    const rootComb = comps[k].combinator;
    // A sibling combinator into the root is only carriable when the root compound can be
    // the band's own section; otherwise the subject would be outside the band.
    if ((rootComb === '+' || rootComb === '~') && !compoundCanBeRoot(comps[k].text)) return null;

    const ctx = joinCompounds([...comps.slice(0, k), { text: '*', combinator: rootComb }]);
    const root = getSourceRoot();
    if (!root) {
      throw new BandRefusal(
        `the source styles this band through ${JSON.stringify(arm)}, but its page context cannot be verified without a band id; a band swap cannot carry it`,
      );
    }
    // Ask the source DOM first: does the band actually sit in this context? A rule that
    // names another band's ancestor/id (`#other … .x`) does not, so it never styled this
    // band — drop it. Only a context that really applies is carried or refused. A context
    // that depends on page state (`:has()`, `:hover`) is tested in its widened stateless
    // form, so a state the static page is not in cannot hide a rule that styles the band.
    const unsafe = unsafeContextReason(ctx);
    const matched = matchesContext(root, unsafe ? statelessContext(ctx) : ctx);
    if (matched === 'error') {
      throw new BandRefusal(
        `the source styles this band through ${JSON.stringify(arm)}, whose page context ${JSON.stringify(ctx)} is not a selector a band swap can verify; it cannot carry it`,
      );
    }
    if (!matched) return null; // the band never sits in this context in the source: the arm never styles it.
    if (unsafe) {
      throw new BandRefusal(
        `the source styles this band through ${JSON.stringify(arm)}, whose page context ${JSON.stringify(ctx)} depends on ${unsafe} that a band swap cannot re-verify; it cannot carry it`,
      );
    }

    enforceConditionRefusals(joinCompounds(comps.slice(k)));
    contexts.add(ctx);
    return carryWithContext(comps, k);
  };

  /** Like keepArm but a condition-refusal counts as "would travel" for detection, not a throw. */
  const armWouldTravel = (rawArm: string): boolean => {
    try {
      return keepArm(rawArm) !== null;
    } catch (e) {
      if (e instanceof BandRefusal) return true;
      throw e;
    }
  };

  const processRule = (prelude: string, body: string): string | null => {
    const keptArms: string[] = [];
    for (const arm of splitTopLevel(prelude, ',')) {
      const scoped = keepArm(arm);
      if (scoped !== null) keptArms.push(scoped);
    }
    if (!keptArms.length) return null;
    for (const v of varsRead(body)) readVars.add(v);
    for (const m of body.matchAll(/\banimation(?:-name)?\s*:\s*([^;]+)/g)) {
      for (const word of m[1].split(/[\s,]+/)) if (word) animations.add(word);
    }
    // Fragment URLs (`url(#paint)`, `url("#paint")`) point at inner ids that were renamed;
    // re-point them. The root id maps to itself and unknown ids stay verbatim.
    const emitted = body.replace(/url\(\s*(['"]?)#([\w-]+)\1\s*\)/gi, (whole, q: string, id: string) => {
      const next = idRename.get(id);
      return next ? `url(${q}#${next}${q})` : whole;
    });
    return `${keptArms.join(', ')}{${emitted}}`;
  };

  /** True if any style rule anywhere in a container body would travel for this band. */
  const containerTravels = (text: string): boolean => {
    for (const block of cssBlocks(text)) {
      const body = block.body;
      const prelude = effectiveSelector(block.prelude);
      if (/^@keyframes\b/i.test(prelude)) continue;
      if (prelude.startsWith('@')) { if (containerTravels(body)) return true; continue; }
      const armsTravel = splitTopLevel(prelude, ',').some((a) => armWouldTravel(a));
      if (armsTravel) return true;
      if (body.includes('{') && containerTravels(body)) return true;
    }
    return false;
  };

  const processBlocks = (text: string): string[] => {
    const out: string[] = [];
    for (const block of cssBlocks(text)) {
      const body = block.body;
      const prelude = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(prelude)) {
        const inner = processBlocks(body);
        if (inner.length) out.push(`${prelude}{${inner.join('\n')}}`);
      } else if (/^@keyframes\b/i.test(prelude)) {
        continue; // carried below, and only when a kept rule names it
      } else if (/^@(font-face|import|charset|property)\b/i.test(prelude)) {
        continue; // deliberately not carried
      } else if (prelude.startsWith('@')) {
        // Unknown at-rule (@layer/@container/@scope/…): refuse if its prelude names the
        // root id or any travelling arm sits inside it.
        const namesRoot =
          rootId !== '' && [...idTokensOf(prelude), ...attrSelectorTokens(prelude).ids].includes(rootId);
        if (namesRoot || containerTravels(body)) {
          throw new BandRefusal(
            `the source styles this band inside ${firstAtName(prelude)}, which a band swap cannot carry yet`,
          );
        }
      } else if (body.includes('{')) {
        // Nested rule: refuse when the outer OR an inner arm would style the band
        // (e.g. `main { .hero__title { … } }`), never skip it silently.
        if (splitTopLevel(prelude, ',').some((a) => armWouldTravel(a)) || containerTravels(body)) {
          throw new BandRefusal('the source styles this band inside a nested rule, which a band swap cannot carry yet');
        }
      } else {
        const rule = processRule(prelude, body);
        if (rule) out.push(rule);
      }
    }
    return out;
  };

  let kept: string[];
  try {
    kept = processBlocks(css);
  } catch (e) {
    if (e instanceof BandRefusal) return { ok: false, reason: e.reason };
    throw e;
  }

  const keyframeRename = new Map<string, string>();
  try {
    // Inline styles in the band markup read custom properties and may name @keyframes.
    const inlineAnimations = new Set<string>();
    const inlineProps = new Set<string>();
    const inlineStyles = tokenizeTags(fragment).flatMap((t) =>
      t.kind === 'start' ? t.attrs.filter((a) => a.nameLower === 'style' && a.value).map((a) => a.value as string) : [],
    );
    for (const decl of inlineStyles) {
      for (const v of varsRead(decl)) readVars.add(v);
      for (const dm of decl.matchAll(/(--[\w-]+)\s*:/g)) inlineProps.add(dm[1]);
      for (const am of decl.matchAll(/\banimation(?:-name)?\s*:\s*([^;]+)/gi)) {
        for (const word of am[1].split(/[\s,]+/)) if (word) inlineAnimations.add(word);
      }
    }

    // @keyframes (top-level or inside @media/@supports) carried when a carried rule or an
    // inline animation names it, wrapped in its condition; its body joins the var closure.
    const carried = new Set<string>();
    for (const kf of collectKeyframes(css)) {
      if (!animations.has(kf.name) && !inlineAnimations.has(kf.name)) continue;
      carried.add(kf.name);
      keyframeRename.set(kf.name, `${namespace}-${kf.name}`);
      kept.push(wrapConditions(`@keyframes ${namespace}-${kf.name}{${kf.body}}`, kf.conditions));
      for (const v of varsRead(kf.body)) readVars.add(v);
    }
    if (carried.size) {
      for (const [index, rule] of kept.entries()) {
        kept[index] = rule.replace(
          /\banimation(-name)?\s*:\s*([^;}]+)/g,
          (whole, suffix: string, value: string) => {
            const next = value
              .split(/([\s,]+)/)
              .map((word) => (carried.has(word) ? `${namespace}-${word}` : word))
              .join('');
            return `animation${suffix ?? ''}:${next}`;
          },
        );
      }
    }

    // The custom properties the band reads, resolved transitively through every
    // declaration (unconditional + conditional) that supplies them.
    const uncondDecls = rootDeclarations(css);
    const condDecls = conditionalRootDeclarations(css);
    const valuesOf = new Map<string, string[]>();
    const addValue = (name: string, value: string): void => {
      const list = valuesOf.get(name);
      if (list) list.push(value); else valuesOf.set(name, [value]);
    };
    for (const [name, value] of uncondDecls) addValue(name, value);
    for (const { name, value } of condDecls) addValue(name, value);
    const needed = new Set<string>();
    const queue = [...readVars];
    while (queue.length) {
      const name = queue.pop() as string;
      if (needed.has(name)) continue;
      needed.add(name);
      for (const value of valuesOf.get(name) ?? []) queue.push(...varsRead(value));
    }

    // The custom properties the band itself supplies (its own carried rules or inline styles); those
    // travel with the band and are not borrowed from anywhere.
    const bandSupplied = new Set<string>(inlineProps);
    for (const rule of kept) for (const dm of rule.matchAll(/(--[\w-]+)\s*:/g)) bandSupplied.add(dm[1]);

    // A var the band reads from an ancestor rule other than :root (body, html, a `.page` wrapper) —
    // and does not supply itself — would silently take the DESTINATION's value on apply, since the
    // swap carries only :root; an ancestor declaration also outranks a carried :root one. Refuse,
    // naming the var and its supplier. (No carry attempted: fail closed.)
    const ancestorSuppliers = ancestorVarSuppliers(css, getSourceRoot());
    for (const name of needed) {
      if (bandSupplied.has(name)) continue;
      const supplier = ancestorSuppliers.get(name);
      if (supplier) {
        throw new BandRefusal(
          `the band reads ${name}, which the source sets on an ancestor through ${JSON.stringify(supplier)}; only plain :root declarations travel with a band, so a band swap cannot carry it`,
        );
      }
    }

    // The marker block below emits every unconditional value ahead of the conditional ones, which
    // preserves the source cascade only when, per var, all unconditional declarations precede all
    // conditional ones in source. If an unconditional declaration follows a conditional one, emitting
    // unconditional-first would REVERSE the precedence and change the winning value — refuse instead.
    const orderedRoot = rootDeclarationsInOrder(css);
    for (const name of needed) {
      const at = orderedRoot.flatMap((d, k) => (d.name === name ? [{ cond: d.conditions.length > 0, k }] : []));
      const condK = at.filter((x) => x.cond).map((x) => x.k);
      const uncondK = at.filter((x) => !x.cond).map((x) => x.k);
      if (condK.length && uncondK.length && Math.max(...uncondK) > Math.min(...condK)) {
        throw new BandRefusal(
          `the source declares ${name} on :root both unconditionally and under a media/supports condition, in an order whose precedence a band swap cannot preserve, so a band swap cannot carry it`,
        );
      }
    }

    // @property is a global registration a band cannot scope: refuse if the band depends on one.
    const hitProp = [...propertyRegistrations(css)].find((p) => needed.has(p) || inlineProps.has(p));
    if (hitProp) {
      throw new BandRefusal(
        `the source registers ${hitProp} with @property, a global registration a band swap cannot scope to the band; nothing was changed`,
      );
    }

    // Unconditional declarations first (sorted), then each conditional declaration in source
    // order wrapped in its condition on the marker, so the cascade matches the source.
    const uncondPairs = [...needed]
      .filter((n) => uncondDecls.has(n))
      .sort()
      .map((name) => `${name}:${uncondDecls.get(name) as string}`);
    if (uncondPairs.length) kept.unshift(`${marker}{${uncondPairs.join(';')}}`);
    for (const { conditions, name, value } of condDecls) {
      if (needed.has(name)) kept.push(wrapConditions(`${marker}{${name}:${value}}`, conditions));
    }
  } catch (e) {
    if (e instanceof BandRefusal) return { ok: false, reason: e.reason };
    throw e;
  }

  if (!kept.length) {
    return { ok: false, reason: 'no rule in the source draft targets this band, so there is nothing to carry' };
  }

  // A <script> or inline on*= handler that names a private class/inner-id this snapshot
  // renames would break once it renames it; refuse rather than carry stale script bytes.
  const scriptDep = scriptDependencyRefusal(
    fragment,
    new Set(classRename.keys()),
    new Set([...idRename].filter(([k, v]) => k !== v).map(([k]) => k)),
  );
  if (scriptDep) return { ok: false, reason: scriptDep };

  // The band's own in-band <style> rules already travelled into the scoped owned block;
  // drop those elements from the markup so nothing lands unscoped in the destination.
  const renamed = renameMarkup(stripInBandStyles(fragment), { classRename, idRename, rootId, marker: namespace, keyframeRename });

  const carriedCss = kept.join('\n');
  const transport = transportTypography(doc, carriedCss, namespace, buildTypographyFingerprint(doc, fragment, carriedCss, bandId));
  const html = renameInlineFonts(renamed, transport.typography.transport?.privateFamilies ?? {});
  return {
    ok: true,
    snapshot: {
      namespace,
      bandId,
      tenantId,
      html,
      css: transport.css,
      typography: transport.typography,
      ...(contexts.size ? { contexts: [...contexts] } : {}),
    },
  };
}

/**
 * Rewrite a band fragment: mark the section, rename private names, re-point every
 * kind of id reference. Every rewrite goes through the tag tokenizer's attribute
 * value spans, so text nodes, shortcodes (`[gravityform id="1"]`), comments and
 * script/style text stay byte-identical. Unknown (not-renamed) references stay
 * verbatim, and the original quote (or unquoted) form is kept.
 *
 * `keyframeRename` (the carried @keyframes, built by `snapshotBand`) renames
 * `animation`/`animation-name` names inside `style` attribute values.
 */
function renameMarkup(
  fragment: string,
  maps: {
    classRename: Map<string, string>;
    idRename: Map<string, string>;
    rootId: string;
    marker: string;
    keyframeRename?: Map<string, string>;
  },
): string {
  const { classRename, idRename, marker, keyframeRename } = maps;
  // Insert the marker attribute on the band's root element, whatever its tag
  // (`<section>`, `<header>`, `<footer>`, …), just before its closing `>`.
  const root = tokenizeTags(fragment).find((t): t is StartTag => t.kind === 'start');
  const at = root ? root.end - (root.selfClosing ? 2 : 1) : -1;
  const marked = at < 0 ? fragment : `${fragment.slice(0, at)} ${WRAPPER_ATTR}="${marker}"${fragment.slice(at)}`;
  return applyMarkupRewrite(marked, {
    renameClass: (c) => classRename.get(c) ?? c,
    renameId: (id) => idRename.get(id) ?? id, // rootId maps to itself
    renameKeyframe: keyframeRename ? (k) => keyframeRename.get(k) ?? k : undefined,
  });
}

/** Escape a string for transport inside a double-quoted HTML attribute. */
function escapeAttr(s: string): string {
  // `>` too: the owned <style> open tag is also found by `<style\b[^>]*>` scans.
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/** Reverse `escapeAttr`. */
function unescapeAttr(s: string): string {
  return s.replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&quot;/g, '"').replace(/&amp;/g, '&');
}

/**
 * True when an owned-CSS selector arm places a page-context compound before the band
 * marker (`.lp--form-first *[data-fav-band="ns"]#hero`) — i.e. the rules only applied
 * when the band sat in that context. Used to decide whether a legacy import (one with
 * no recorded `data-fav-contexts`) can be safely re-snapshotted.
 */
function ownedCssHasContextArm(css: string): boolean {
  const marker = `[${WRAPPER_ATTR}=`;
  const scan = (text: string): boolean => {
    for (const { prelude, body } of cssBlocks(text)) {
      const sel = prelude.trimStart();
      if (sel.startsWith('@')) {
        if (/^@(media|supports)\b/i.test(sel) && scan(body)) return true;
        continue; // @keyframes and other at-rules carry no band marker
      }
      for (const arm of splitTopLevel(prelude, ',')) {
        const comps = compounds(arm.trim());
        if (comps.findIndex((c) => c.text.includes(marker)) > 0) return true; // a compound stands before the marker
      }
    }
    return false;
  };
  return scan(css);
}

/**
 * A carried `<script>` body or inline `on*=` handler whose text names a class or id
 * this swap renames would break once the swap renames it (the tokenizer never rewrites
 * script text or handler code). Refuse, naming the dependency, rather than treating the
 * unchanged bytes as compatible. JSON-LD (`type="application/ld+json"`) is data, not
 * code: only a `#id` fragment naming a renamed id counts. Returns a reason, or null.
 */
function scriptDependencyRefusal(fragment: string, renamedClasses: Set<string>, renamedIds: Set<string>): string | null {
  const hit = (text: string, names: Set<string>): string | null => {
    for (const name of names) {
      const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (new RegExp(`(^|[^\\w-])${esc}([^\\w-]|$)`).test(text)) return name;
    }
    return null;
  };
  for (const tag of tokenizeTags(fragment)) {
    if (tag.kind !== 'start') continue;
    for (const a of tag.attrs) {
      if (!a.nameLower.startsWith('on') || a.value === null) continue;
      const h = hit(a.value, renamedClasses) ?? hit(a.value, renamedIds);
      if (h) return `the band's ${a.name}= handler references ${JSON.stringify(h)}, a name this swap renames, so the handler would break; a band swap cannot carry it`;
    }
  }
  for (const m of fragment.matchAll(/<script\b([^>]*)>([\s\S]*?)<\/script>/gi)) {
    const type = /\btype\s*=\s*["']?([^"'\s>]+)/i.exec(m[1])?.[1]?.toLowerCase() ?? '';
    if (type === 'application/ld+json') {
      for (const idm of m[2].matchAll(/#(-?[_a-zA-Z][\w-]*)/g)) {
        if (renamedIds.has(idm[1])) return `the band's JSON-LD references "#${idm[1]}", an id this swap renames; a band swap cannot carry it`;
      }
      continue;
    }
    const h = hit(m[2], renamedClasses) ?? hit(m[2], renamedIds);
    if (h) return `the band's <script> references ${JSON.stringify(h)}, a name this swap renames, so the script would break; a band swap cannot carry it`;
  }
  return null;
}

/**
 * A quoted string or a bracketed `[…]` attribute selector (group 1: skipped verbatim), else a
 * `.class`/`#id` token (groups 2-3). The rebase rewrites only the latter, so a value such as
 * `[data-note=".old-x"]` or `content:".old-x"` is left alone; `[id=…]`/`[class~=…]` are rebased
 * by their own exact-token pass.
 */
const SIGIL_OUTSIDE_STRINGS = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\[[^\]]*\])|([.#])(-?[_a-zA-Z][\w-]*)/g;

/**
 * Rebase an owned stylesheet from `oldNs` to `newNs` by EXACT token maps, never by
 * prefix. Classes, ids and @keyframes get SEPARATE maps, so a class token can never
 * override a canonical id: the root id (excluded from `idMap`) is left untouched even
 * when a class shares its spelling (`#final-cta` vs `.final-cta` under namespace
 * `final`). Rewrites `.class`, `#id`, `[class~="x"]`/`[id="x"]` attribute selectors
 * (exact token only), `@keyframes name`, `url(#id)`, `animation:` names, and the
 * `[data-fav-band]` marker.
 */
function rebaseCss(
  css: string,
  oldNs: string,
  newNs: string,
  classMap: Map<string, string>,
  idMap: Map<string, string>,
  keyframeMap: Map<string, string>,
): string {
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return css
    .replace(new RegExp(`\\[${WRAPPER_ATTR}=(["'])${esc(oldNs)}\\1\\]`, 'g'), `[${WRAPPER_ATTR}="${newNs}"]`)
    .replace(SIGIL_OUTSIDE_STRINGS, (whole, quoted: string | undefined, sigil: string, tok: string) => {
      if (quoted !== undefined) return whole; // a string or `[attr=…]` value: not a selector token
      const to = (sigil === '#' ? idMap : classMap).get(tok);
      return to ? `${sigil}${to}` : whole;
    })
    // `[id="inner"]` / `[class~="x"]` track the same rename as `#inner` / `.x`; exact
    // token only (risky operators never reach owned CSS — the snapshot refused them).
    .replace(
      /\[\s*(id|class)\s*([~]?=)\s*(?:"([^"]*)"|'([^']*)'|([^\]\s"'=]+))\s*([iIsS])?\s*\]/g,
      (whole, attr: string, op: string, dq: string, sq: string, uq: string, flag: string) => {
        const raw = dq ?? sq ?? uq ?? '';
        const to = (attr.toLowerCase() === 'id' ? idMap : classMap).get(raw);
        return to ? `[${attr}${op}"${to}"${flag ? ` ${flag}` : ''}]` : whole;
      },
    )
    .replace(/(@keyframes\s+)(-?[_a-zA-Z][\w-]*)/gi, (whole, kw: string, tok: string) => {
      const to = keyframeMap.get(tok);
      return to ? `${kw}${to}` : whole;
    })
    .replace(/url\(\s*(['"]?)#([\w-]+)\1\s*\)/gi, (whole, q: string, tok: string) => {
      const to = idMap.get(tok);
      return to ? `url(${q}#${to}${q})` : whole;
    })
    .replace(/\banimation(-name)?\s*:\s*([^;}]+)/gi, (whole, suffix: string, value: string) => {
      const next = value
        .split(/([\s,]+)/)
        .map((word) => keyframeMap.get(word) ?? word)
        .join('');
      return `animation${suffix ?? ''}:${next}`;
    });
}

/** Re-snapshot an already-imported band from its own owned stylesheet. */
function reSnapshot(args: {
  doc: string;
  fragment: string;
  bandId: string;
  tenantId: string;
  namespace: string;
  oldNs: string;
}): SnapshotResult {
  const { doc, fragment, bandId, tenantId, namespace, oldNs } = args;
  const wantSlot = bandId.trim().toLowerCase();
  // Require EXACTLY ONE owned <style> for this namespace+slot: 0 cannot prove the
  // band's styling, 2+ cannot decide which stylesheet is the band's.
  const owned: string[] = [];
  const ownedOpen: string[] = [];
  for (const s of styleSpans(doc)) {
    if (styleNamespace(s) === oldNs && styleSlot(s)?.trim().toLowerCase() === wantSlot) {
      owned.push(doc.slice(s.bodyStart, s.bodyEnd));
      ownedOpen.push(doc.slice(s.start, s.bodyStart));
    }
  }
  if (owned.length === 0) {
    return {
      ok: false,
      reason: 'this band was brought in from another draft and its own stylesheet is missing, so its styling cannot be proven',
    };
  }
  if (owned.length > 1) {
    return {
      ok: false,
      reason: `this band carries ${owned.length} owned stylesheets for its slot, so which one styles it cannot be decided; nothing was changed`,
    };
  }
  // Resets belong to the old destination, not the source band being moved again.
  const sourceAt = owned[0].indexOf('/* fav-source */');
  const ownedCss = sourceAt < 0 ? owned[0] : owned[0].slice(sourceAt + '/* fav-source */'.length);
  const savedType = /\bdata-fav-typography\s*=\s*"([^"]*)"/i.exec(ownedOpen[0])?.[1];
  const typography = savedType ? readTypography(unescapeAttr(savedType)) : buildTypographyFingerprint(doc, fragment, ownedCss, bandId);
  if (!typography) return { ok: false, reason: 'the imported band carries unreadable typography provenance; nothing was changed' };

  // Recover the page-context contract this band carried when it was imported. A new
  // import stamps it on the owned <style> as `data-fav-contexts`; carry it through the
  // re-snapshot verbatim (contexts name page ancestors, not band-private tokens, so a
  // namespace rebase never touches them). A legacy import that lacks the attribute but
  // whose owned rules still carry a page-context arm cannot be proven safe to re-lift.
  const ctxRaw = /\bdata-fav-contexts\s*=\s*"([^"]*)"/i.exec(ownedOpen[0])?.[1];
  let contexts: string[] | undefined;
  if (ctxRaw !== undefined) {
    try {
      const parsed = JSON.parse(unescapeAttr(ctxRaw)) as unknown;
      if (Array.isArray(parsed) && parsed.every((c) => typeof c === 'string')) contexts = parsed as string[];
    } catch { contexts = undefined; }
  } else if (ownedCssHasContextArm(ownedCss)) {
    return {
      ok: false,
      reason:
        'this band was brought in from another draft and its page-context requirements were not recorded, so re-snapshotting could drop rules that only applied in that context; a band swap cannot carry it',
    };
  }
  const withContexts = <T extends BandSnapshot>(snap: T): T =>
    contexts && contexts.length ? { ...snap, contexts } : snap;

  if (namespace === oldNs) {
    return { ok: true, snapshot: withContexts({ namespace, bandId, tenantId, html: fragment, css: ownedCss, typography }) };
  }

  // The exact set of private tokens to rebase, in SEPARATE maps so a class token can
  // never override the canonical id: every class token, every inner id (all ids except
  // the canonical root id), and every @keyframes name. Each already carries the `oldNs-`
  // prefix, so the rebase is a literal head swap. The root id, excluded here, is never
  // rewritten even when a class shares its spelling.
  const rootStart = tokenizeTags(fragment).find((t): t is StartTag => t.kind === 'start');
  const rootId = (rootStart?.attrs.find((a) => a.nameLower === 'id')?.value ?? '').trim();
  const classMap = new Map<string, string>();
  const idMap = new Map<string, string>();
  const keyframeMap = new Map<string, string>();
  const rebased = (tok: string): string => `${namespace}-${tok.slice(oldNs.length + 1)}`;
  for (const c of classesIn(fragment)) if (c.startsWith(`${oldNs}-`)) classMap.set(c, rebased(c));
  for (const id of idsIn(fragment)) if (id !== rootId && id.startsWith(`${oldNs}-`)) idMap.set(id, rebased(id));
  const span = scanBands(doc).find(b => b.id === bandId);
  const outside = span ? doc.slice(0, span.start) + doc.slice(span.end) : '';
  for (const id of idMap.keys()) {
    if (scriptDependencyRefusal(outside, new Set(), new Set([id]))) idMap.delete(id);
  }
  for (const m of ownedCss.matchAll(/@keyframes\s+(-?[_a-zA-Z][\w-]*)/gi)) if (m[1].startsWith(`${oldNs}-`)) keyframeMap.set(m[1], rebased(m[1]));

  // A carried <script> or inline on*= handler whose text names one of the tokens this
  // rebase renames would break the moment it renames it; refuse rather than move it.
  const scriptDep = scriptDependencyRefusal(fragment, new Set(classMap.keys()), new Set(idMap.keys()));
  if (scriptDep) return { ok: false, reason: scriptDep };

  const families: Record<string, string> = {};
  if (typography.transport) {
    for (const [family, oldName] of Object.entries(typography.transport.privateFamilies)) {
      const newName = `fav-${namespace}-${family}`;
      families[oldName.toLowerCase()] = newName;
      typography.transport.privateFamilies[family] = newName;
    }
  }
  const css = renameFontFamilies(rebaseCss(ownedCss, oldNs, namespace, classMap, idMap, keyframeMap), families);
  // Rebase the marker value on the band's root element, whatever its tag, by its
  // exact byte span rather than a `<section`-anchored regex.
  const markAttr = rootStart?.attrs.find((a) => a.nameLower === WRAPPER_ATTR);
  const marked =
    markAttr && markAttr.value !== null
      ? fragment.slice(0, markAttr.valueStart) + namespace + fragment.slice(markAttr.valueEnd)
      : fragment;
  const html = renameInlineFonts(applyMarkupRewrite(marked, {
    renameClass: (t) => classMap.get(t) ?? t,
    renameId: (t) => idMap.get(t) ?? t,
    renameKeyframe: (t) => keyframeMap.get(t) ?? t,
  }), families);
  return { ok: true, snapshot: withContexts({ namespace, bandId, tenantId, html, css, typography }) };
}

function renameInlineFonts(html: string, families: Record<string, string>): string {
  if (!Object.keys(families).length) return html;
  let out = html;
  const attrs = tokenizeTags(html).flatMap((t) => t.kind === 'start' ? t.attrs.filter((a) => a.nameLower === 'style' && a.value !== null) : []);
  for (const a of attrs.reverse()) {
    const css = renameFontFamilies(`x{${unescapeAttr(a.value ?? '')}}`, families);
    const body = css.slice(css.indexOf('{') + 1, -1);
    out = out.slice(0, a.valueStart) + body.replace(/"/g, '&quot;').replace(/'/g, '&#39;') + out.slice(a.valueEnd);
  }
  return out;
}

function readTypography(text: string): TypographyFingerprint | null {
  try {
    const t = JSON.parse(text) as TypographyFingerprint;
    const strings = (x: unknown): x is string[] => Array.isArray(x) && x.every((v) => typeof v === 'string');
    const record = (x: unknown): boolean => x !== null && typeof x === 'object' && !Array.isArray(x) && Object.values(x).every((v) => typeof v === 'string');
    if (!t || typeof t.inherited !== 'string' || !strings(t.usedFamilies) || !record(t.faces) || !strings(t.bandTags) || !strings(t.pageLevel) || !strings(t.unknownLinks)) return null;
    if (t.transport && (typeof t.transport.rootSize !== 'string' || !record(t.transport.privateFamilies) || !strings(t.transport.refusal) || !strings(t.transport.inheritedProperties) || !strings(t.transport.unresolvedVariables))) return null;
    return t;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// applyBandSnapshot
// ---------------------------------------------------------------------------

/**
 * The byte span of every REAL `<style>` element, via the tag tokenizer — so a
 * `<style>`-looking run inside a `<script>` or an HTML comment is never seen as a
 * stylesheet. `body` is the raw CSS between the tags; `open`/`close` are the tag
 * bytes so a rewrite can keep them verbatim. Partitioning, removal and insertion of
 * `<style>` in apply all go through this, never a whole-document regex.
 */
type StyleSpan = {
  tag: StartTag;
  start: number;
  bodyStart: number;
  bodyEnd: number;
  end: number;
};
function styleSpans(html: string): StyleSpan[] {
  const tags = tokenizeTags(html);
  const out: StyleSpan[] = [];
  for (let i = 0; i < tags.length; i += 1) {
    const t = tags[i];
    if (t.kind !== 'start' || t.name !== 'style') continue;
    const endTag = tags.slice(i + 1).find((x) => x.kind === 'end' && x.name === 'style');
    out.push({ tag: t, start: t.start, bodyStart: t.end, bodyEnd: endTag ? endTag.start : html.length, end: endTag ? endTag.end : html.length });
  }
  return out;
}
const styleIsOwned = (s: StyleSpan): boolean => s.tag.attrs.some((a) => a.nameLower === WRAPPER_ATTR);
const styleNamespace = (s: StyleSpan): string | undefined => s.tag.attrs.find((a) => a.nameLower === WRAPPER_ATTR)?.value ?? undefined;
const styleSlot = (s: StyleSpan): string | undefined => s.tag.attrs.find((a) => a.nameLower === SLOT_ATTR)?.value ?? undefined;

/** Rebuild `html`, replacing each real `<style>` element with `fn`'s return (its own bytes when unchanged). */
function rewriteStyleSpans(html: string, fn: (s: StyleSpan, own: string) => string): string {
  let out = '';
  let cursor = 0;
  for (const s of styleSpans(html)) {
    out += html.slice(cursor, s.start);
    out += fn(s, html.slice(s.start, s.end));
    cursor = s.end;
  }
  return out + html.slice(cursor);
}

/**
 * Byte index at which to insert a slot's owned `<style>`, always OUTSIDE every top-level
 * band span. Inserting after the last `</style>` (the old strategy) can land the block
 * inside a band when a writer's last stylesheet sits in the footer; the next swap of that
 * band then removes it with the band span. So place it right after the last PAGE-LEVEL
 * (non-band) `<style>` — keeping the owned block last in source order among page styles —
 * else after the last band, else at the end.
 */
function ownedInsertIndex(html: string): number {
  const bands = scanBands(html);
  const inBand = (pos: number): boolean => bands.some((b) => pos >= b.start && pos < b.end);
  const pageStyles = styleSpans(html).filter((s) => !inBand(s.start));
  if (pageStyles.length) return pageStyles[pageStyles.length - 1].end;
  if (bands.length) return bands[bands.length - 1].end;
  return html.length;
}

/**
 * Selector arms of every plain style rule in `css`, recursing into `@media`/`@supports`.
 * Rules the probe cannot judge (inside `@layer`/`@container`/`@scope`/any other container, or
 * holding nested rules) go to `opaque` as text, so the caller can refuse when they name a changed token.
 */
function selectorArmsIn(css: string, opaque: string[]): string[] {
  const arms: string[] = [];
  const walk = (text: string): void => {
    for (const block of cssBlocks(text)) {
      const sel = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(sel)) { walk(block.body); continue; }
      const holdsRules = nextTopBrace(block.body, 0) >= 0;
      if (sel.startsWith('@')) {
        if (holdsRules && !/^@(-[a-z]+-)?keyframes\b/i.test(sel)) opaque.push(`${sel}{${block.body}}`);
        continue; // @keyframes/@font-face/... — no element selector
      }
      if (holdsRules) { opaque.push(`${sel}{${block.body}}`); continue; }
      for (const a of splitTopLevel(sel, ',')) { const t = a.trim(); if (t) arms.push(t); }
    }
  };
  walk(css);
  return arms;
}

/**
 * Does this arm reference a class/id token the swap changes? A swap only alters the band's
 * OWN class/id tokens (renamed into the namespace), so an arm naming none of them matches the
 * same outside-band elements before and after — the only DOM change outside the band is nil.
 * Skipping those arms is exact, not a heuristic, and it keeps the behavioural check to the
 * handful of arms that can actually be collateral. A risky substring/prefix operator on id or
 * class is kept (its match cannot be ruled out token-for-token).
 */
function armReferencesChanged(arm: string, changed: Set<string>): boolean {
  for (const m of arm.matchAll(/[.#](-?[_a-zA-Z][\w-]*)/g)) if (changed.has(m[1])) return true;
  const at = attrSelectorTokens(arm);
  if ([...at.ids, ...at.classes].some((t) => changed.has(t))) return true;
  return at.risky.some((r) => r.attr === 'id' || r.attr === 'class');
}

/** A selector safe to hand to querySelectorAll: pseudo-elements dropped (they attach to a real element, so the element matches). */
function qsaSafe(arm: string): string | null {
  const s = arm
    .replace(/::[-\w]+(\([^)]*\))?/g, '')
    .replace(/:(?:before|after|first-line|first-letter)\b/gi, '')
    .trim();
  return s || null;
}

/** Splice `data-fav-probe="k"` onto every real element start tag OUTSIDE `span`, numbering non-`<style>` elements in document order. */
function markOutsideBand(html: string, span: { start: number; end: number }): string {
  const edits: Array<{ at: number; text: string }> = [];
  let k = 0;
  for (const t of tokenizeTags(html)) {
    if (t.kind !== 'start' || t.name === 'style') continue; // styles: count/text differ across a swap, so they carry no probe
    if (t.start >= span.start && t.start < span.end) continue; // inside the band — its elements legitimately change
    edits.push({ at: t.end - (t.selfClosing ? 2 : 1), text: ` data-fav-probe="${k}"` });
    k += 1;
  }
  edits.sort((a, b) => b.at - a.at);
  let out = html;
  for (const e of edits) out = out.slice(0, e.at) + e.text + out.slice(e.at);
  return out;
}

/** Probe numbers of the elements a selector matches that carry a probe (i.e. real elements outside the band). */
function probeMatches(root: Document, sel: string): Set<string> {
  const out = new Set<string>();
  for (const el of Array.from(root.querySelectorAll(sel))) {
    const p = el.getAttribute('data-fav-probe');
    if (p !== null) out.add(p);
  }
  return out;
}

/**
 * G4 collateral-damage guard (oracle-05 (c), first). The swap renames the outgoing band's
 * root/private classes, so a RETAINED destination rule whose subject lies OUTSIDE the band
 * but which depended on those classes as a sibling/ancestor silently stops matching its
 * neighbour (`.sheet .band + .band` styled the NEXT band because THIS band carried `.band`).
 * Behavioural test: mark every element outside the band in both the destination and the
 * composed page, then for each retained destination arm compare the set of OUTSIDE-band
 * elements it matches. Any difference is real collateral damage → refuse, naming the arm.
 * ponytail: selectors querySelectorAll cannot parse (exotic pseudos) are skipped, not the
 * plain structural selectors this guards; a nested-rule arm is skipped as elsewhere.
 */
function neighbourDamageRefusal(
  doc: string,
  composed: string,
  outgoing: { start: number; end: number },
  incoming: { start: number; end: number },
  retainedArms: Set<string>,
  changed: Set<string>,
): string | null {
  const arms = [...retainedArms].filter((arm) => armReferencesChanged(arm, changed));
  if (!arms.length) return null;
  const before = new DOMParser().parseFromString(markOutsideBand(doc, outgoing), 'text/html');
  const after = new DOMParser().parseFromString(markOutsideBand(composed, incoming), 'text/html');
  for (const arm of arms) {
    // A static document never hovers or focuses, so `.band:hover + .band` matches nothing on
    // either side; also compare its stateless form so a state-dependent neighbour rule counts.
    for (const sel of new Set([qsaSafe(arm), qsaSafe(statelessContext(arm))])) {
      if (!sel) continue;
      let b: Set<string>;
      let a: Set<string>;
      try {
        b = probeMatches(before, sel);
        a = probeMatches(after, sel);
      } catch {
        continue; // querySelectorAll cannot parse this selector; not the plain structural case this guards
      }
      if (b.size !== a.size || [...b].some((p) => !a.has(p))) {
        return `the swap changes the band's classes, so the retained destination rule ${JSON.stringify(arm)} no longer styles the same elements outside the band; nothing was changed`;
      }
    }
  }
  return null;
}

/** Does this arm's structure make it OWNED by the outgoing band (targets `#rootId`, no reach-out)? */
function armIsOwnedBy(arm: string, rootId: string): boolean {
  const comps = compounds(arm);
  let last = -1;
  comps.forEach((c, i) => {
    // `[id="hero"]` / `[id~="hero"]` names the root id just as `#hero` does.
    if ([...compoundTokens(c.text).ids, ...attrSelectorTokens(c.text).ids].includes(rootId)) last = i;
  });
  if (last < 0) return false;
  const after = comps[last + 1]?.combinator;
  return after === undefined || after === ' ' || after === '>';
}

/** True when the root id is entangled in a top-level :is()/:where()/:has()/… — ownership cannot be proven. */
function armRefusesSplit(arm: string, rootId: string): boolean {
  const re = /:(?:is|where|matches|-webkit-any|has)\(/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(arm)) !== null) {
    const openIdx = m.index + m[0].length - 1;
    const end = matchParen(arm, openIdx);
    const inner = arm.slice(openIdx + 1, end);
    if ([...idTokensOf(inner), ...attrSelectorTokens(inner).ids].includes(rootId)) return true;
    re.lastIndex = end;
  }
  return false;
}

/** True if any arm anywhere in a container references the root id (owned or entangled). */
function bodyReferencesRoot(css: string, rootId: string): boolean {
  let i = 0;
  while (i < css.length) {
    const open = nextTopBrace(css, i);
    if (open < 0) break;
    const prelude = css.slice(i, open);
    const close = matchBrace(css, open);
    const body = css.slice(open + 1, close);
    // Classify with leading comments stripped so `/* c */@media{…}` is still seen as an at-rule.
    const trimmed = stripLeadingComments(prelude).trim();
    if (trimmed.startsWith('@')) {
      if (!/^@keyframes\b/i.test(trimmed) && bodyReferencesRoot(body, rootId)) return true;
    } else {
      const arms = splitTopLevel(prelude, ',').map((a) => a.trim());
      if (arms.some((a) => armIsOwnedBy(a, rootId) || armRefusesSplit(a, rootId))) return true;
      if (nextTopBrace(body, 0) >= 0 && bodyReferencesRoot(body, rootId)) return true;
    }
    i = close + 1;
  }
  return false;
}

/** Remove arms owned by `rootId` from a prelude; null when none survive. Refuses on unsplittable arms. */
function partitionArms(prelude: string, rootId: string, retired: string[]): string | null {
  const arms = splitTopLevel(prelude, ',');
  const keptArms: string[] = [];
  for (const arm of arms) {
    const t = arm.trim();
    if (!t) { keptArms.push(arm); continue; }
    // The retired label is the selector as written, minus any leading comment trivia.
    const label = stripLeadingComments(arm).trim();
    // A risky attribute operator on id that could match the band id can't be classified as
    // owned-or-not, so it can't be retired safely.
    for (const r of attrSelectorTokens(t).risky) {
      if (r.attr === 'id' && attrOpCouldMatch(r.op, r.val, rootId)) {
        throw new BandRefusal(
          `the destination may style #${rootId} through ${JSON.stringify(label)}, whose [${r.attr}${r.op}"${r.val}"] a swap cannot resolve safely; nothing was changed`,
        );
      }
    }
    if (armRefusesSplit(t, rootId)) {
      throw new BandRefusal(
        `the destination styles #${rootId} through ${JSON.stringify(label)}, which a swap cannot safely split; nothing was changed`,
      );
    }
    if (armIsOwnedBy(t, rootId)) { retired.push(label); continue; }
    keptArms.push(arm);
  }
  return keptArms.length ? keptArms.join(',') : null;
}

/** Rewrite a <style> body, retiring the outgoing band's own rules by offset. Everything else stays byte-identical. */
function stripOwnedArms(css: string, rootId: string, retired: string[]): string {
  let out = '';
  let i = 0;
  while (i < css.length) {
    const open = nextTopBrace(css, i);
    if (open < 0) { out += css.slice(i); break; }
    const rawPrelude = css.slice(i, open);
    const close = matchBrace(css, open);
    const body = css.slice(open + 1, close);
    // Leading `;`-statements (@charset/@import/...) share this byte span with the
    // rule's real selector. Emit them verbatim; classify only the selector.
    const { statements, selector: prelude } = splitLeadingStatements(rawPrelude);
    out += statements;
    // Classify with leading comments stripped (a `/* c */` before `@media` must not
    // hide the at-rule); the original `prelude` text, comment included, is still emitted.
    const trimmed = stripLeadingComments(prelude).trim();
    if (/^@(media|supports)\b/i.test(trimmed)) {
      const before = retired.length;
      const inner = stripOwnedArms(body, rootId, retired);
      // Drop a container only when a retirement emptied it; an originally-empty
      // container survives byte-identical.
      const emptiedByRetirement = retired.length > before && nextTopBrace(inner, 0) < 0;
      if (!emptiedByRetirement) out += `${prelude}{${inner}}`;
    } else if (trimmed.startsWith('@')) {
      // An at-rule whose PRELUDE names the root (`@scope (#hero)`) is invisible to a
      // body scan; refuse it explicitly.
      const preludeNamesRoot =
        rootId !== '' && [...idTokensOf(prelude), ...attrSelectorTokens(prelude).ids].includes(rootId);
      if (preludeNamesRoot) {
        throw new BandRefusal(
          `the destination styles #${rootId} inside ${firstAtName(trimmed)}, whose prelude names the band; a swap cannot safely split it; nothing was changed`,
        );
      }
      if (/^@(keyframes|font-face|page|charset|import|property|namespace)\b/i.test(trimmed)) {
        out += `${prelude}{${body}}`;
      } else if (bodyReferencesRoot(body, rootId)) {
        throw new BandRefusal(
          `the destination styles #${rootId} inside ${firstAtName(trimmed)}, which a swap cannot safely split; nothing was changed`,
        );
      } else {
        out += `${prelude}{${body}}`;
      }
    } else if (nextTopBrace(body, 0) >= 0) {
      // nested style rule (nested CSS): the root may be named on an INNER arm under an
      // unrelated outer selector (`main { #hero h1 { … } }`), so scan the body too.
      const arms = splitTopLevel(prelude, ',').map((a) => a.trim());
      if (arms.some((a) => armIsOwnedBy(a, rootId) || armRefusesSplit(a, rootId)) || bodyReferencesRoot(body, rootId)) {
        throw new BandRefusal(
          `the destination styles #${rootId} inside a nested rule, which a swap cannot safely split; nothing was changed`,
        );
      }
      out += `${prelude}{${body}}`;
    } else {
      const newPrelude = partitionArms(prelude, rootId, retired);
      if (newPrelude !== null) out += `${newPrelude}{${body}}`;
      // else all arms owned; drop the whole rule
    }
    i = close + 1;
  }
  return out;
}

/**
 * How many REAL elements carry each id, keyed case-insensitively (the one id-case
 * rule; see scanBands and the slot). Only exact `id` attributes on real elements
 * count — `data-id` is not an id, and ids in comments/scripts/styles/shortcodes
 * are not elements — so a repeated `data-id` no longer looks like a duplicate id.
 */
function idCounts(html: string): Map<string, number> {
  const out = new Map<string, number>();
  for (const tag of tokenizeTags(html)) {
    if (tag.kind !== 'start') continue;
    const id = tag.attrs.find((a) => a.nameLower === 'id')?.value?.trim().toLowerCase();
    if (id) out.set(id, (out.get(id) ?? 0) + 1);
  }
  return out;
}

/** Owned `<style data-fav-band … data-fav-slot=slot>` blocks for a slot (a commented-out one is skipped). */
function ownedBlocksForSlot(html: string, slot: string): number {
  let n = 0;
  for (const tag of tokenizeTags(html)) {
    if (tag.kind !== 'start' || tag.name !== 'style') continue;
    const hasBand = tag.attrs.some((a) => a.nameLower === 'data-fav-band');
    const s = tag.attrs.find((a) => a.nameLower === 'data-fav-slot')?.value;
    if (hasBand && s === slot) n += 1;
  }
  return n;
}

/**
 * Every id REFERENCE on a page that resolves to no element id: fragment/token-list/
 * single-id attributes, `url(#…)` in any attribute value, and `url(#…)` in `<style>`
 * bodies. Empty `#` and `#top` are ignored. HTML id references are CASE-SENSITIVE, so a
 * reference resolves only against an element id of the SAME case (`href="#Hero"` does not
 * resolve to `id="hero"`); keys are the exact ids. Case-insensitivity is kept only for band
 * ids (idCounts, scanBands, the slot), not for reference resolution. The value keeps one
 * sample occurrence, for the refusal message.
 */
function danglingRefs(html: string): Map<string, IdRef> {
  const ids = idsIn(html); // exact, case-sensitive element ids — id references resolve exactly
  const out = new Map<string, IdRef>();
  const consider = (ref: IdRef): void => {
    const id = ref.id.trim();
    if (!id || id.toLowerCase() === 'top') return;
    if (!ids.has(id) && !out.has(id)) out.set(id, ref);
  };
  for (const ref of collectAttrRefs(html)) consider(ref);
  // <style> bodies are raw text (the tokenizer skips them); read them from real element spans.
  for (const s of styleSpans(html)) {
    const body = html.slice(s.bodyStart, s.bodyEnd);
    for (const u of body.matchAll(/url\(\s*['"]?#([\w-]+)['"]?\s*\)/gi)) {
      consider({ id: u[1], attr: 'style', sample: `url(#${u[1]})`, start: s.bodyStart + (u.index ?? 0) });
    }
  }
  return out;
}

/**
 * The band carries page-context requirements (`.lp--form-first *`) from its source.
 * Refuse the apply unless the composed page places the band's root the same way, so a
 * rule that only styled the band under a wrapper never lands where that wrapper is absent.
 */
function contextsHold(composed: string, namespace: string, contexts?: string[]): string | null {
  if (!contexts || !contexts.length) return null;
  // The owned <style> block also carries the marker; the band's root is the marked NON-style element.
  const root = new DOMParser()
    .parseFromString(composed, 'text/html')
    .querySelector(`[${WRAPPER_ATTR}="${namespace}"]:not(style)`);
  if (!root) return null; // no marked root to test; the structural validation below handles that.
  for (const ctx of contexts) {
    if (matchesContext(root, ctx) === true) continue;
    const { ancestor, relation } = describeContext(ctx);
    return `the source styles this band only when it sits ${relation} ${JSON.stringify(ancestor)}, and this draft does not place it there; nothing was changed`;
  }
  return null;
}

/**
 * The OUTGOING band's own in-band `<style>` elements vanish when its span is replaced.
 * That is only safe when every rule in them styles the outgoing band itself (so removing
 * them touches nothing else on the page) and any `@keyframes` they define is used nowhere
 * else. Each style-rule arm — recursing into `@media`/`@supports` — must be owned by the
 * outgoing band (`armIsOwnedBy`: names `#<band>` as its subject, no reach-out); a
 * non-owned arm (`h2`, `:root{--x}`, `#trust p`, `body`), `@font-face`, `@import`, a
 * nested rule or any other at-rule cannot be proven band-local, so it refuses by name. A
 * `@keyframes` referenced by `animation`/`animation-name` outside the band's span refuses.
 * A `snapshot.html` that itself carries a `<style>` (an external/legacy object we never
 * scoped) also refuses. Returns a named reason, or null when the removal is safe.
 */
function outgoingInBandStyleRefusal(
  doc: string,
  outgoing: { start: number; end: number },
  rootId: string,
  snapshotHtml: string,
): string | null {
  if (tokenizeTags(snapshotHtml).some((t) => t.kind === 'start' && t.name === 'style')) {
    return 'this snapshot carries a <style> element in its markup, so it was not scoped by a band swap and cannot be placed safely; nothing was changed';
  }

  const fragment = doc.slice(outgoing.start, outgoing.end);
  const tags = tokenizeTags(fragment);
  const definedKeyframes = new Set<string>();
  let refusal: string | null = null;

  const nameAtRule = (text: string): string => firstAtName(stripLeadingComments(text).trim());

  const walk = (css: string): void => {
    if (refusal) return;
    if (importUrls(css).length) {
      refusal = 'the band being replaced carries a <style> with an @import, which styles more than that band, so removing it would change the rest of the page; nothing was changed';
      return;
    }
    for (const block of cssBlocks(css)) {
      if (refusal) return;
      const { statements } = splitLeadingStatements(block.prelude);
      if (statements.trim()) {
        refusal = `the band being replaced carries a <style> with ${nameAtRule(statements)}, which styles more than that band, so removing it would change the rest of the page; nothing was changed`;
        return;
      }
      const sel = effectiveSelector(block.prelude);
      const kf = /^@keyframes\s+(-?[_a-zA-Z][\w-]*)/i.exec(sel);
      if (kf) { definedKeyframes.add(kf[1]); continue; }
      if (/^@(media|supports)\b/i.test(sel)) { walk(block.body); continue; }
      if (sel.startsWith('@')) {
        refusal = `the band being replaced carries a <style> with ${nameAtRule(sel)}, which styles more than that band, so removing it would change the rest of the page; nothing was changed`;
        return;
      }
      if (nextTopBrace(block.body, 0) >= 0) {
        refusal = 'the band being replaced carries a <style> with a nested rule a swap cannot prove styles only that band, so removing it would change the rest of the page; nothing was changed';
        return;
      }
      for (const arm of splitTopLevel(sel, ',')) {
        const t = arm.trim();
        if (!t) continue;
        if (!armIsOwnedBy(t, rootId)) {
          refusal = `the band being replaced carries a <style> whose rule ${JSON.stringify(t)} styles more than that band, so removing it would change the rest of the page; nothing was changed`;
          return;
        }
      }
    }
  };

  for (let i = 0; i < tags.length; i += 1) {
    const t = tags[i];
    if (t.kind !== 'start' || t.name !== 'style') continue;
    if (t.attrs.some((a) => a.nameLower === WRAPPER_ATTR)) continue; // owned block, replaced separately
    const endTag = tags.slice(i + 1).find((x) => x.kind === 'end' && x.name === 'style');
    walk(stripComments(fragment.slice(t.end, endTag ? endTag.start : fragment.length)));
    if (refusal) return refusal;
  }

  // A keyframe defined in-band disappears with the band; refuse if any element elsewhere
  // on the page animates with it. `[^;}]+` mirrors the animation parsing used elsewhere.
  // ponytail: over-refuses the rare case where the SAME name is also defined at page level;
  // writer bands name each keyframe uniquely per band, so this does not fire in practice.
  if (definedKeyframes.size) {
    const rest = withoutInertMarkup(doc.slice(0, outgoing.start) + doc.slice(outgoing.end));
    for (const m of rest.matchAll(/\banimation(?:-name)?\s*:\s*([^;}]+)/gi)) {
      for (const word of m[1].split(/[\s,]+/)) {
        if (definedKeyframes.has(word)) {
          return `the band being replaced defines @keyframes ${JSON.stringify(word)} in its <style>, which is used elsewhere on the page, so removing it would change the rest of the page; nothing was changed`;
        }
      }
    }
  }
  return null;
}

/**
 * Drop a snapshot into another draft of the SAME tenant, in the same slot.
 *
 * Refuses across tenants. The scoping fix makes a cross-tenant band render
 * correctly, which is precisely why it must not be reachable: the band would
 * arrive carrying the source client's city, phone, claims and form shortcode
 * with nothing visibly broken to warn anyone.
 *
 * The outgoing band's own destination rules (`#hero h1`) are retired arm by
 * arm before the incoming stylesheet lands, the owned block is replaced per
 * slot rather than appended, and the composed page is validated before return.
 */
export function applyBandSnapshot(args: {
  doc: string;
  bandId: string;
  tenantId: string;
  snapshot: BandSnapshot;
}): ApplyResult {
  const { doc, bandId, tenantId, snapshot } = args;
  if (snapshot.tenantId !== tenantId) {
    return {
      ok: false,
      reason:
        'this band was saved from a different client, and a band carries that client’s copy with it, so it cannot be used here',
    };
  }
  const wanted = bandId.trim().toLowerCase();
  if (snapshot.bandId.trim().toLowerCase() !== wanted) {
    return { ok: false, reason: `this snapshot carries the ${JSON.stringify(snapshot.bandId)} band, not ${JSON.stringify(bandId)}` };
  }
  const snapBands = scanBands(snapshot.html);
  // The band's root is the snapshot's first start tag, whatever band tag it is.
  const snapStart = tokenizeTags(snapshot.html).find((t): t is StartTag => t.kind === 'start' && BAND_TAGS.has(t.name));
  const snapMark = snapStart?.attrs.find((a) => a.nameLower === 'data-fav-band')?.value;
  if (snapBands.length !== 1 || snapBands[0].id !== wanted || snapMark !== snapshot.namespace) {
    return { ok: false, reason: 'this snapshot is not a single marked band section, so it cannot be placed safely' };
  }
  // The band element must BE the whole snapshot: only whitespace around it, no outer
  // wrapper and no sibling markup, or a supplied snapshot could smuggle a wrapper
  // div or extra elements into the destination.
  if (!/^\s*$/.test(snapshot.html.slice(0, snapBands[0].start)) || !/^\s*$/.test(snapshot.html.slice(snapBands[0].end))) {
    return { ok: false, reason: 'this snapshot has markup outside its band section, so it cannot be placed safely' };
  }

  const matches = scanBands(doc).filter((b) => b.id === wanted);
  if (matches.length === 0) return { ok: false, reason: `this draft has no band with id ${JSON.stringify(bandId)} to replace` };
  if (matches.length > 1) return { ok: false, reason: `the draft carries ${matches.length} bands with id ${JSON.stringify(bandId)}` };
  for (const s of styleSpans(doc)) {
    if (!styleIsOwned(s) && selectorUsesEscape(stripComments(doc.slice(s.bodyStart, s.bodyEnd)))) return { ok: false, reason: ESCAPED_SELECTOR_REASON };
  }

  // A namespace owns one slot's block. If the destination already carries an owned block for a
  // DIFFERENT slot under this snapshot's namespace, the incoming band's marker `[data-fav-band=ns]`
  // would also match that other slot's marked root and its block: reject the reuse.
  for (const s of styleSpans(doc)) {
    const slot = styleSlot(s);
    if (styleNamespace(s) === snapshot.namespace && slot !== undefined && slot !== wanted) {
      return {
        ok: false,
        reason: `the destination already uses the namespace ${JSON.stringify(snapshot.namespace)} for its ${JSON.stringify(slot)} band, and one namespace cannot own two slots; nothing was changed`,
      };
    }
  }

  // The outgoing band's own in-band <style> is removed with its span; refuse unless every
  // rule in it styles the outgoing band itself and its @keyframes are used nowhere else.
  const inBandRefusal = outgoingInBandStyleRefusal(doc, matches[0], wanted, snapshot.html);
  if (inBandRefusal) return { ok: false, reason: inBandRefusal };

  // Retire the outgoing band's destination rules in every NON-owned <style>. The outgoing
  // band's own in-band <style> is skipped: it leaves with the band's span (checked above).
  // Real element spans (not a whole-document regex), so a <style>-looking run inside a
  // <script> or comment is never partitioned.
  const retired: string[] = [];
  let refusal: string | null = null;
  const outgoing = matches[0];
  const outside = doc.slice(0, outgoing.start) + doc.slice(outgoing.end);
  const incomingIds = idsIn(snapshot.html);
  for (const id of idsIn(doc.slice(outgoing.start, outgoing.end))) {
    if (!incomingIds.has(id) && scriptDependencyRefusal(outside, new Set(), new Set([id]))) {
      return { ok: false, reason: `an outside script or handler references ${JSON.stringify(id)}, which this swap would remove; nothing was changed` };
    }
  }
  const partitioned = rewriteStyleSpans(doc, (s, own) => {
    if (styleIsOwned(s)) return own; // owned block, byte-identical
    if (s.start >= outgoing.start && s.start < outgoing.end) return own; // the outgoing band's own <style>
    try {
      const open = doc.slice(s.start, s.bodyStart);
      const close = doc.slice(s.bodyEnd, s.end);
      return `${open}${stripOwnedArms(doc.slice(s.bodyStart, s.bodyEnd), wanted, retired)}${close}`;
    } catch (e) {
      if (e instanceof BandRefusal) { refusal = refusal ?? e.reason; return own; }
      throw e;
    }
  });
  if (refusal) return { ok: false, reason: refusal };

  // Replace the band span with the snapshot, at the same slot.
  const span = scanBands(partitioned).find((b) => b.id === wanted);
  if (!span) return { ok: false, reason: `this draft has no band with id ${JSON.stringify(bandId)} to replace` };
  const swapped = partitioned.slice(0, span.start) + snapshot.html + partitioned.slice(span.end);

  // One owned block per slot: remove any existing block for this slot (by real element span),
  // add the fresh one below.
  const cleaned = rewriteStyleSpans(swapped, (s, own) => (styleSlot(s) === wanted ? '' : own));

  // Judge remaining typography risks on the page as swapped. Safe destination-only
  // element rules receive scoped reverts before the source rules, never global edits.
  const gate = checkTypographyGate(snapshot, cleaned);
  if (!gate.ok) return { ok: false, reason: gate.reason, detail: gate.detail };

  // Collateral-damage guard: refuse when renaming the band's classes changes which OUTSIDE-band
  // elements a retained destination rule styles (e.g. `.sheet .band + .band` stops matching the
  // next band). Judged on the page as swapped, before the owned <style> is inserted.
  const incoming = scanBands(cleaned).find((b) => b.id === wanted);
  if (incoming) {
    const retainedArms = new Set<string>();
    const opaque: string[] = [];
    for (const s of styleSpans(cleaned)) {
      if (styleIsOwned(s)) continue;
      for (const a of selectorArmsIn(stripComments(cleaned.slice(s.bodyStart, s.bodyEnd)), opaque)) retainedArms.add(a);
    }
    // Tokens the swap changes: the outgoing band's own classes/ids vanish, the incoming band's
    // (namespaced) ones appear. Only arms naming one of these can shift an outside-band match.
    const outFrag = doc.slice(outgoing.start, outgoing.end);
    const changed = new Set<string>([
      ...classesIn(outFrag), ...idsIn(outFrag),
      ...classesIn(snapshot.html), ...idsIn(snapshot.html),
    ]);
    const hidden = opaque.find((block) => armReferencesChanged(block, changed));
    if (hidden) {
      return {
        ok: false,
        reason: `the destination styles the page inside ${JSON.stringify(hidden.slice(0, hidden.indexOf('{')).trim())} with a rule that depends on a class or id this swap changes; a band swap cannot verify it; nothing was changed`,
      };
    }
    const damage = neighbourDamageRefusal(doc, cleaned, outgoing, incoming, retainedArms, changed);
    if (damage) return { ok: false, reason: damage };
  }

  // Insert the slot's owned <style> OUTSIDE every band span, so a later swap of another band
  // cannot carry it away with that band's markup.
  const ctxAttr = snapshot.contexts && snapshot.contexts.length ? ` data-fav-contexts="${escapeAttr(JSON.stringify(snapshot.contexts))}"` : '';
  const typeAttr = ` data-fav-typography="${escapeAttr(JSON.stringify(snapshot.typography))}"`;
  const ownedBlock = `<style ${WRAPPER_ATTR}="${snapshot.namespace}" ${SLOT_ATTR}="${wanted}"${ctxAttr}${typeAttr}>${gate.resetCss ?? ''}/* fav-source */${snapshot.css}</style>`;
  const insertAt = ownedInsertIndex(cleaned);
  const composed = cleaned.slice(0, insertAt) + ownedBlock + cleaned.slice(insertAt);

  // Every OTHER slot's owned <style> that the destination carried must survive byte-identical:
  // if a band replacement carried one away (an owned block that had landed inside a band), refuse.
  for (const s of styleSpans(doc)) {
    if (!styleIsOwned(s)) continue;
    const slot = styleSlot(s);
    if (slot === undefined || slot === wanted) continue;
    if (!composed.includes(doc.slice(s.start, s.end))) {
      return {
        ok: false,
        reason: `the swap would drop the owned style block another swap placed for the ${JSON.stringify(slot)} band; nothing was changed`,
      };
    }
  }

  // The carried rules may require the band to sit in a page context; refuse
  // here (before structural validation) if this draft does not place it there.
  const ctxRefusal = contextsHold(composed, snapshot.namespace, snapshot.contexts);
  if (ctxRefusal) return { ok: false, reason: ctxRefusal };

  // Validate the composed page; never return broken html.
  const bands = scanBands(composed).filter((b) => b.id === wanted);
  if (bands.length !== 1) {
    return { ok: false, reason: `the swap would leave ${bands.length} bands with id ${JSON.stringify(bandId)}; nothing was changed` };
  }
  const counts = idCounts(composed);
  if ((counts.get(wanted) ?? 0) !== 1) {
    return { ok: false, reason: `the swap would leave ${counts.get(wanted) ?? 0} elements with id="${wanted}"; nothing was changed` };
  }
  const dup = [...counts.entries()].find(([, c]) => c > 1);
  if (dup) {
    const preExisting = (idCounts(doc).get(dup[0]) ?? 0) > 1;
    return {
      ok: false,
      reason: preExisting
        ? `the draft already carries two elements with id="${dup[0]}", so the swap cannot be validated; nothing was changed`
        : `the swap would leave two elements with id="${dup[0]}"; nothing was changed`,
    };
  }
  if (ownedBlocksForSlot(composed, wanted) !== 1) {
    return {
      ok: false,
      reason: `the swap would leave ${ownedBlocksForSlot(composed, wanted)} owned style blocks for ${JSON.stringify(bandId)}; nothing was changed`,
    };
  }
  // The swap must not orphan an id reference. Any reference
  // that dangles in the composed page but not in the original destination is NEW —
  // a destination link into the outgoing band's inner ids, or a band reference to
  // an id the destination lacks. Refuse and name it; pre-existing dangles are kept.
  const wasDangling = danglingRefs(doc);
  for (const [id, ref] of danglingRefs(composed)) {
    if (wasDangling.has(id)) continue;
    const origin = ref.start >= bands[0].start && ref.start < bands[0].end ? 'in the swapped band' : 'elsewhere on the page';
    return {
      ok: false,
      reason: `the swap would leave ${ref.sample} ${origin} pointing at "#${id}", which no element on the page defines; nothing was changed`,
    };
  }

  return { ok: true, html: composed, retired };
}
